import { useState, useEffect } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Calendar,
  MapPin,
  Clock,
  Mail,
  Copy,
  Check,
  ExternalLink,
  ArrowUp,
  Users,
  Share,
  QrCode,
  CalendarPlus,
  Eye,
  Edit3,
  XCircle,
  LogOut,
  Link2,
  Plus,
  Trash2,
  UserPlus,
  Send,
  Mic,
  LayoutDashboard,
  Lock,
  Unlock,
  ChevronRight,
  RefreshCw,
  CheckCircle2,
  MessageSquare,
  Globe,
  Music,
  ShoppingBag,
  Archive
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import PhygitalIcon from "@/components/PhygitalIcon";
import UnifiedPreferenceCard from "@/components/UnifiedPreferenceCard";
import SpeakerIntakeCard from "@/components/SpeakerIntakeCard";
import ConfirmationStageBanner from "@/components/confirmation/ConfirmationStageBanner";
import PageMeta from "@/components/PageMeta";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { TAP_IN_COVER } from "@/lib/integrationConfig";
import { getCategoryPreset } from "@/lib/categoryConfig";
import { useUiSurface, slotText } from "@/hooks/useUiSurface";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Invisible disabilities ID to label and description mapping
const INVISIBLE_DISABILITIES_MAP = {
  'vocal_tics': { label: 'Vocal tics', desc: 'I may make sudden sounds or verbal expressions' },
  'motor_tics': { label: 'Motor tics', desc: 'I may have sudden movements or gestures' },
  'tourettes': { label: "Tourette's Syndrome", desc: 'Combination of vocal and motor tics' },
  'anxiety': { label: 'Anxiety', desc: 'I may need extra patience or breaks' },
  'chronic_pain': { label: 'Chronic pain', desc: 'I may need to adjust position frequently' },
  'chronic_fatigue': { label: 'Chronic fatigue', desc: 'I may need rest breaks during the event' },
  'adhd': { label: 'ADHD', desc: 'I may need reminders or have difficulty with long sessions' },
  'autism': { label: 'Autism spectrum', desc: 'I may have specific sensory or communication preferences' },
  'epilepsy': { label: 'Epilepsy', desc: 'Please be aware of potential seizures' }
};

// Get human-readable info for invisible disability
const getInvisibleDisabilityInfo = (id) => INVISIBLE_DISABILITIES_MAP[id] || { label: id, desc: '' };

// Format time to 12-hour format
const formatTime12Hour = (time, timezone) => {
  if (!time) return "";
  if (time.toUpperCase().includes('AM') || time.toUpperCase().includes('PM')) {
    if (timezone) return `${time} ${timezone}`;
    return time;
  }
  let hours, minutes;
  if (time.includes(':')) {
    [hours, minutes] = time.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return time;
  } else {
    return time;
  }
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  const formatted = `${hours}:${String(minutes).padStart(2, '0')} ${ampm}`;
  if (timezone) return `${formatted} ${timezone}`;
  return formatted;
};

const getLocalTimezoneAbbr = () => {
  try {
    return new Date().toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ').pop() || '';
  } catch { return ''; }
};

const getTimezoneAbbr = (tzNameOrId) => {
  if (!tzNameOrId) return getLocalTimezoneAbbr();
  if (/^[A-Z]{2,5}$/.test(tzNameOrId)) return tzNameOrId;
  try {
    const abbr = new Date().toLocaleTimeString('en-US', { timeZone: tzNameOrId, timeZoneName: 'short' }).split(' ').pop();
    if (abbr && /^[A-Z]/.test(abbr)) return abbr;
  } catch {}
  const map = {
    'Pacific Time': 'PT', 'Eastern Time': 'ET', 'Central Time': 'CT', 'Mountain Time': 'MT',
    'Pacific Standard Time': 'PST', 'Eastern Standard Time': 'EST', 'Central Standard Time': 'CST',
    'Pacific Daylight Time': 'PDT', 'Eastern Daylight Time': 'EDT',
    'GMT': 'GMT', 'UTC': 'UTC', 'IST': 'IST'
  };
  return map[tzNameOrId] || tzNameOrId;
};

// Safe date formatter - never returns "Invalid Date"
const formatSafeDate = (dateStr, opts = { month: "short", day: "numeric", year: "numeric" }) => {
  if (!dateStr) return null;
  const raw = String(dateStr).split('T')[0];
  const [year, month, day] = raw.split('-');
  if (!year || !month || !day || isNaN(Number(year))) return null;
  const d = new Date(Number(year), Number(month) - 1, Number(day));
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', opts);
};

// Get image URL helper
const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  // Use base URL, not API (avoid double /api/ prefix)
  const baseUrl = process.env.REACT_APP_BACKEND_URL;
  return `${baseUrl}${path}`;
};

// Ensure absolute URL
const ensureAbsoluteUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('//')) return 'https:' + url;
  return 'https://' + url;
};

// Normalize preference string for comparison (handles different formats)
// e.g., "vegetarian" -> "vegetarian", "Vegetarian" -> "vegetarian", "gluten_free" -> "glutenfree"
const normalizePreference = (str) => {
  if (!str) return '';
  return str.toLowerCase().replace(/[-_\s]/g, '');
};

// Check if a preference list includes an item (case-insensitive, format-agnostic)
const prefListIncludes = (list, item) => {
  if (!list || !Array.isArray(list)) return false;
  const normalizedItem = normalizePreference(item);
  return list.some(listItem => normalizePreference(listItem) === normalizedItem);
};

// Normalize an array of preferences to display format
const normalizePreferencesToDisplay = (list) => {
  if (!list || !Array.isArray(list)) return [];
  // Map common variations to display format
  const displayMap = {
    'vegetarian': 'Vegetarian',
    'vegan': 'Vegan',
    'glutenfree': 'Gluten-Free',
    'gluten_free': 'Gluten-Free',
    'dairyfree': 'Dairy-Free',
    'dairy_free': 'Dairy-Free',
    'nutallergy': 'Nut Allergy',
    'nut_allergy': 'Nut Allergy',
    'kosher': 'Kosher',
    'halal': 'Halal',
    'chicken': 'Chicken',
    'beef': 'Beef',
    'pork': 'Pork',
    'lamb': 'Lamb',
    'fish': 'Fish',
    'shellfish': 'Shellfish',
    'nomeat': 'No Meat',
    'no_meat': 'No Meat',
    'peanuts': 'Peanuts',
    'treenuts': 'Tree Nuts',
    'tree_nuts': 'Tree Nuts',
    'nuts': 'Tree Nuts',
    'eggs': 'Eggs',
    'milk': 'Milk',
    'dairy': 'Milk',
    'soy': 'Soy',
    'wheat': 'Wheat',
    'sesame': 'Sesame',
    'wheelchairaccess': 'Wheelchair Access',
    'wheelchair': 'Wheelchair Access',
    'signlanguage': 'Sign Language',
    'sign_language': 'Sign Language',
    'visualassistance': 'Visual Assistance',
    'visual_assistance': 'Visual Assistance',
    'hearingassistance': 'Hearing Assistance',
    'hearing_assistance': 'Hearing Assistance',
    'hearing': 'Hearing Assistance',
    'quietspace': 'Quiet Space',
    'quiet_space': 'Quiet Space',
    'serviceanimal': 'Service Animal',
    'service_animal': 'Service Animal'
  };
  
  return list.map(item => {
    const normalized = normalizePreference(item);
    return displayMap[normalized] || displayMap[item.toLowerCase()] || item;
  });
};

export default function ConfirmationPage() {
  const { rsvpId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Iter 9.47.i - Confirmation page UI copy is now driven by Airtable
  // surface `surface_confirmation`. Edit slots there to rephrase phase
  // labels, share/edit CTAs, and section titles platform-wide.
  const { slotMap: cnf } = useUiSurface("surface_confirmation");
  const viewMode = searchParams.get('view'); // 'preferences' for read-only preference card view
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [inviteLinkCopied, setInviteLinkCopied] = useState(false);
  const [showCalendarOptions, setShowCalendarOptions] = useState(false);
  const [showPreferenceCard, setShowPreferenceCard] = useState(viewMode === 'preferences'); // Auto-open if view=preferences
  const [showCantMakeItDialog, setShowCantMakeItDialog] = useState(false);
  const [cancellingRsvp, setCancellingRsvp] = useState(false);
  const [showEditPreferencesModal, setShowEditPreferencesModal] = useState(false);
  const [expandedImage, setExpandedImage] = useState(null);
  const [editingPreferences, setEditingPreferences] = useState({});
  const [savingPreferences, setSavingPreferences] = useState(false);
  const [showSignOutDialog, setShowSignOutDialog] = useState(false);
  const [vipListData, setVipListData] = useState(null);
  const [guestInviteLinkCopied, setGuestInviteLinkCopied] = useState(false);
  
  // Extra invite request state
  const [showExtraInviteModal, setShowExtraInviteModal] = useState(false);
  const [extraInviteCount, setExtraInviteCount] = useState(2);
  const [extraInviteReason, setExtraInviteReason] = useState("");
  const [requestingExtraInvites, setRequestingExtraInvites] = useState(false);
  const [extraInviteRequested, setExtraInviteRequested] = useState(false);
  
  // Manage Guest List Modal state
  const [showManageGuestListModal, setShowManageGuestListModal] = useState(false);
  const [guestListMembers, setGuestListMembers] = useState([]);
  const [loadingGuestList, setLoadingGuestList] = useState(false);
  const [newPlaceholderName, setNewPlaceholderName] = useState("");
  const [addingPlaceholder, setAddingPlaceholder] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [isEventHost, setIsEventHost] = useState(false); // Track if user is host/cohost
  
  // Self check-in state
  const [isCheckedIn, setIsCheckedIn] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  
  // Resources state (for instructor events - unlocked after check-in)
  const [resourcesData, setResourcesData] = useState(null);
  
  // Service upsells state (linked classes, products from the service)
  const [serviceUpsells, setServiceUpsells] = useState([]);
  const [serviceAddons, setServiceAddons] = useState([]);
  const [selectingUpsell, setSelectingUpsell] = useState(null);
  
  // Dynamic page content state
  const [pageContent, setPageContent] = useState({});
  const [customLinks, setCustomLinks] = useState([]);
  
  // Filter out list_host from member counts - they don't count against allocation
  // List host gets their spots "free" - if they have 10 invites, they're the 11th person
  const guestListMembersForCount = guestListMembers.filter(m => m.role !== 'list_host');
  const guestListMembersToDisplay = guestListMembers.filter(m => m.role !== 'list_host');
  
  // Calculate TOTAL attendees including +1s/+2s brought by guests
  // Primary VIPs: those NOT invited by another member (count against VIP limit)
  const primaryVIPs = guestListMembersForCount.filter(m => !m.vip_invited_by && !m.invited_by_member_id);
  // Plus ones: those invited by another member OR counted via legacy plus_count_used field
  const plusOnesAsMembers = guestListMembersForCount.filter(m => m.vip_invited_by || m.invited_by_member_id);
  const legacyPlusOnes = primaryVIPs.reduce((sum, m) => sum + (m.plus_count_used || 0), 0);
  const totalPlusOnes = Math.max(plusOnesAsMembers.length, legacyPlusOnes);
  const totalAttendeesCount = primaryVIPs.length + totalPlusOnes;

  // WebSocket connection for real-time VIP list updates
  useEffect(() => {
    if (!showManageGuestListModal || !data?.vip_info?.list_id) return;
    
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${wsProtocol}//${API.replace(/^https?:\/\//, '')}/ws/vip-list/${data.vip_info.list_id}`;
    
    let ws = null;
    let reconnectAttempts = 0;
    const maxReconnectAttempts = 3;
    
    const connectWebSocket = () => {
      try {
        ws = new WebSocket(wsUrl);
        
        ws.onopen = () => {
          console.log('VIP WebSocket connected');
          setWsConnected(true);
          reconnectAttempts = 0;
        };
        
        ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            console.log('VIP WebSocket message:', message);
            
            if (message.type === 'member_added' || message.type === 'member_joined' || message.type === 'plus_two_joined') {
              // A new member was added - refresh the list
              fetchGuestListMembers();
              toast.success(`${message.data?.member?.display_name || 'Someone'} joined the VIP list!`);
            } else if (message.type === 'placeholder_claimed') {
              // A placeholder was claimed by someone RSVPing - refresh to show updated status
              fetchGuestListMembers();
              const memberName = message.data?.member?.display_name || 'Someone';
              toast.success(`${memberName} claimed their spot!`);
            } else if (message.type === 'member_removed') {
              // A member was removed - update state directly
              setGuestListMembers(prev => prev.filter(m => m.member_id !== message.data?.member_id));
            } else if (message.type === 'full_refresh') {
              // Full refresh of member list
              setGuestListMembers(message.members || []);
            }
          } catch (e) {
            console.error('WebSocket message parse error:', e);
          }
        };
        
        ws.onerror = (error) => {
          console.error('VIP WebSocket error:', error);
          setWsConnected(false);
        };
        
        ws.onclose = () => {
          console.log('VIP WebSocket disconnected');
          setWsConnected(false);
          
          // Attempt to reconnect
          if (reconnectAttempts < maxReconnectAttempts) {
            reconnectAttempts++;
            setTimeout(connectWebSocket, 2000 * reconnectAttempts);
          }
        };
      } catch (e) {
        console.error('WebSocket connection failed:', e);
      }
    };
    
    connectWebSocket();
    
    return () => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    };
  }, [showManageGuestListModal, data?.vip_info?.list_id]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API}/confirmation/${rsvpId}`);
        if (!res.ok) {
          const errData = await res.json();
          throw new Error(errData.detail || "Failed to load confirmation");
        }
        const result = await res.json();
        setData(result);
        // Set initial check-in state from RSVP data
        setIsCheckedIn(result.rsvp?.status === 'checked_in' || result.rsvp?.checked_in === true);
        
        // Fetch dynamic page content if we have an event
        if (result.event?.event_id) {
          try {
            const contentRes = await fetch(`${API}/events/${result.event.event_id}/page-content?page=confirmation`);
            if (contentRes.ok) {
              const contentData = await contentRes.json();
              setPageContent(contentData.content || {});
              setCustomLinks(contentData.custom_links || []);
            }
          } catch (contentErr) {
            console.log("Page content not available, using defaults");
          }
          
          // Fetch resources for instructor events
          if (result.event?.pilot_category === 'instructor') {
            try {
              const resRes = await fetch(`${API}/events/${result.event.event_id}/resources?rsvp_id=${rsvpId}`);
              if (resRes.ok) {
                const resData = await resRes.json();
                setResourcesData(resData);
              }
            } catch (resErr) {
              console.log("Resources not available");
            }
          }
          
          // Fetch service upsells (linked classes, products)
          if (result.rsvp?.service_id || result.event?.event_id) {
            try {
              const svcRes = await fetch(`${API}/provider/services/${result.event.event_id}`);
              if (svcRes.ok) {
                const svcData = await svcRes.json();
                const allUpsells = [];
                (svcData.services || []).forEach(svc => {
                  (svc.upsells || []).filter(u => u.is_active).forEach(u => {
                    allUpsells.push({ ...u, _service_id: svc.service_id, _service_name: svc.name });
                  });
                });
                setServiceUpsells(allUpsells);
              }
            } catch (e) { /* no upsells available */ }
          }
          
          // Fallback: use event-level upsells if no service upsells found
          if (result.event?.upsells?.length > 0) {
            setServiceUpsells(prev => prev.length > 0 ? prev : result.event.upsells);
          }

          // Iter 9.48 - fetch service add-on products (host brand items
          // attached to this specific provider_service) for confirmation upsell.
          if (result.rsvp?.service_id) {
            try {
              const ar = await fetch(`${API}/api/services/${result.rsvp.service_id}/addons`);
              if (ar.ok) {
                const ad = await ar.json();
                if (Array.isArray(ad.addons) && ad.addons.length > 0) {
                  setServiceAddons(ad.addons);
                }
              }
            } catch (e) { /* no addons available */ }
          }
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [rsvpId]);

  // Check if current user is a host/cohost for this event
  useEffect(() => {
    const checkHostStatus = async () => {
      const token = localStorage.getItem("session_token") || localStorage.getItem("phygital_auth_token");
      if (!token || !data?.event) {
        setIsEventHost(false);
        return;
      }
      
      try {
        // Get current user
        const userRes = await fetch(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!userRes.ok) {
          setIsEventHost(false);
          return;
        }
        const user = await userRes.json();
        const event = data.event;
        
        // Check if user is the main host
        const isMainHost = event.host_user_id === user.user_id;
        
        // Check if user is in co_hosts array
        const isCohost = (event.co_hosts || []).some(ch => 
          ch.user_id === user.user_id || 
          (ch.email && user.email && ch.email.toLowerCase() === user.email.toLowerCase())
        );
        
        // Check if user is in team with host/admin role
        const isTeamHost = (event.team || []).some(m => 
          (m.user_id === user.user_id || (m.email && user.email && m.email.toLowerCase() === user.email.toLowerCase())) &&
          (m.role === 'host' || m.role === 'admin' || m.role === 'owner' || m.label === 'host')
        );
        
        setIsEventHost(isMainHost || isCohost || isTeamHost);
      } catch (err) {
        console.error("Error checking host status:", err);
        setIsEventHost(false);
      }
    };
    
    checkHostStatus();
  }, [data?.event]);

  const copyPassLink = async () => {
    const shareUrl = window.location.href;
    const shareTitle = `My RSVP for ${data?.event?.name || 'Event'}`;
    const shareText = `Check out my confirmation for ${data?.event?.name || 'this event'}!`;
    
    // Use native share if available (mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl
        });
        return;
      } catch (err) {
        // User cancelled or share failed, fall back to copy
        if (err.name !== 'AbortError') {
          console.log('Share failed, copying instead');
        }
      }
    }
    
    // Fallback to clipboard
    navigator.clipboard.writeText(shareUrl);
    setLinkCopied(true);
    toast.success("Link copied!");
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const copyInviteLink = async (url) => {
    const fullUrl = window.location.origin + url;
    const shareTitle = `You're invited to ${data?.event?.name || 'an event'}!`;
    const shareText = `Join me at ${data?.event?.name || 'this event'}!`;
    
    // Use native share if available (mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: fullUrl
        });
        return;
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.log('Share failed, copying instead');
        }
      }
    }
    
    // Fallback to clipboard
    navigator.clipboard.writeText(fullUrl);
    setInviteLinkCopied(true);
    toast.success("Invite link copied!");
    setTimeout(() => setInviteLinkCopied(false), 2000);
  };

  const generateCalendarUrl = (type) => {
    if (!data?.event) return;
    const event = data.event;
    
    const title = encodeURIComponent(event.name || "Event");
    const location = encodeURIComponent(event.venue_name || event.location || "");
    const description = encodeURIComponent(`Your confirmation: ${window.location.href}`);
    
    // Parse date
    let startDate = new Date();
    if (event.date) {
      const [year, month, day] = event.date.split('T')[0].split('-');
      startDate = new Date(year, month - 1, day);
    }
    
    // Parse time
    if (event.start_time) {
      const [hours, minutes] = event.start_time.split(':');
      startDate.setHours(parseInt(hours), parseInt(minutes), 0);
    }
    
    const endDate = new Date(startDate);
    if (event.end_time) {
      const [hours, minutes] = event.end_time.split(':');
      endDate.setHours(parseInt(hours), parseInt(minutes), 0);
    } else {
      endDate.setHours(endDate.getHours() + 3);
    }
    
    const formatDateForCal = (d) => d.toISOString().replace(/-|:|\.\d{3}/g, '').slice(0, 15) + 'Z';
    
    if (type === 'google') {
      return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${formatDateForCal(startDate)}/${formatDateForCal(endDate)}&location=${location}&details=${description}`;
    } else if (type === 'apple') {
      return `data:text/calendar;charset=utf-8,BEGIN:VCALENDAR%0AVERSION:2.0%0ABEGIN:VEVENT%0ADTSTART:${formatDateForCal(startDate)}%0ADTEND:${formatDateForCal(endDate)}%0ASUMMARY:${title}%0ALOCATION:${location}%0ADESCRIPTION:${description}%0AEND:VEVENT%0AEND:VCALENDAR`;
    }
  };


  // Handle selecting a linked upsell (auto-creates RSVP on linked dashboard)
  const handleSelectUpsell = async (upsell) => {
    setSelectingUpsell(upsell.upsell_id);
    try {
      const res = await fetch(`${API}/rsvp/${rsvpId}/select-upsell`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          upsell_id: upsell.upsell_id,
          service_id: upsell._service_id,
        }),
      });
      if (res.ok) {
        const result = await res.json();
        if (result.linked_rsvp_id) {
          // Show success toast or notification
          alert(`Added! ${upsell.name} has been linked. Your preferences have been shared automatically.`);
        } else {
          alert(`${upsell.name} selected!`);
        }
        // Refresh to show updated state
        window.location.reload();
      }
    } catch (e) {
      console.error("Failed to select upsell:", e);
    } finally {
      setSelectingUpsell(null);
    }
  };

  // Handle "Can't Make It" - Cancel RSVP
  const handleCantMakeIt = async () => {
    setCancellingRsvp(true);
    try {
      const res = await fetch(`${API}/rsvps/${rsvpId}/cancel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" }
      });
      
      if (res.ok) {
        toast.success("Your RSVP has been cancelled. We hope to see you at a future event!");
        setData(prev => ({
          ...prev,
          rsvp: { ...prev.rsvp, status: "cant_make_it" }
        }));
        setShowCantMakeItDialog(false);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to cancel RSVP");
      }
    } catch (err) {
      toast.error("Failed to cancel RSVP");
    } finally {
      setCancellingRsvp(false);
    }
  };

  // Edit preferences - open modal instead of navigating
  const handleEditPreferences = () => {
    openEditPreferences();
  };

  // Handle sign out - track and clear page
  const handleSignOut = async () => {
    try {
      // Track sign out for follow-up
      await fetch(`${API}/rsvps/${rsvpId}/signout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" }
      });
    } catch (err) {
      // Tracking failure is non-critical
    }
    
    // Clear ALL auth tokens from browser so RSVP page doesn't auto-detect
    localStorage.removeItem('session_token');
    localStorage.removeItem('phygital_auth_token');
    localStorage.removeItem('phygital_user');
    sessionStorage.clear();
    
    // Clear local data and redirect to event RSVP page for a fresh form
    setData(null);
    toast.success("Signed out successfully");
    const eventSlug = data?.event?.slug || data?.event?.event_id;
    // Use window.location for full page reload to clear React auth state
    window.location.href = eventSlug ? `/e/${eventSlug}` : '/';
  };

  // Self check-in from confirmation page
  const handleSelfCheckIn = async () => {
    setCheckingIn(true);
    try {
      const res = await fetch(`${API}/rsvps/${rsvpId}/self-checkin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" }
      });
      
      if (res.ok) {
        setIsCheckedIn(true);
        setData(prev => ({
          ...prev,
          rsvp: { ...prev.rsvp, status: "checked_in", checked_in: true, checked_in_at: new Date().toISOString() }
        }));
        toast.success("You're checked in! Unlocking your content...");
        
        // Re-fetch resources after check-in (for instructor events)
        if (data?.event?.pilot_category === 'instructor' && data?.event?.event_id) {
          try {
            const resRes = await fetch(`${API}/events/${data.event.event_id}/resources?rsvp_id=${rsvpId}`);
            if (resRes.ok) {
              const resData = await resRes.json();
              setResourcesData(resData);
            }
          } catch (err) {
            console.log("Failed to fetch resources after check-in");
          }
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to check in");
      }
    } catch (err) {
      toast.error("Failed to check in");
    } finally {
      setCheckingIn(false);
    }
  };

  // Copy guest invite link (for VIP list hosts)
  const copyGuestInviteLink = () => {
    const eventSlug = data?.event?.slug || data?.event?.event_id;
    const inviteLink = `${window.location.origin}/e/${eventSlug}`;
    navigator.clipboard.writeText(inviteLink);
    setGuestInviteLinkCopied(true);
    toast.success("Guest invite link copied!");
    setTimeout(() => setGuestInviteLinkCopied(false), 2000);
  };

  // Fetch VIP list members for Manage Guest List modal
  const fetchGuestListMembers = async () => {
    if (!data?.vip_info?.list_id) return;
    setLoadingGuestList(true);
    try {
      const token = localStorage.getItem("session_token");
      // Include requester_rsvp_id for auth when not logged in
      const url = `${API}/events/${data.event.event_id}/vip-lists/${data.vip_info.list_id}/members?requester_rsvp_id=${rsvp?.rsvp_id || ''}`;
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const result = await res.json();
        setGuestListMembers(result.members || []);
      }
    } catch (err) {
      console.error("Failed to fetch guest list:", err);
    } finally {
      setLoadingGuestList(false);
    }
  };

  // Add placeholder member to VIP list
  const addPlaceholderMember = async () => {
    if (!newPlaceholderName.trim()) {
      toast.error("Please enter a name");
      return;
    }
    if (!data?.vip_info?.list_id) return;
    
    setAddingPlaceholder(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/events/${data.event.event_id}/vip-lists/${data.vip_info.list_id}/members`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          name: newPlaceholderName.trim(),
          is_placeholder: true,
          requester_rsvp_id: rsvp?.rsvp_id // Include RSVP ID for auth when not logged in
        })
      });
      
      if (res.ok) {
        toast.success(`Added ${newPlaceholderName}!`);
        setNewPlaceholderName("");
        fetchGuestListMembers();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to add guest");
      }
    } catch (err) {
      toast.error("Failed to add guest");
    } finally {
      setAddingPlaceholder(false);
    }
  };

  // Request extra invites
  const requestExtraInvites = async () => {
    if (!data?.vip_info?.list_id) return;
    setRequestingExtraInvites(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/events/${data.event.event_id}/vip-lists/${data.vip_info.list_id}/request-extra-invites`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          requested_count: extraInviteCount,
          reason: extraInviteReason
        })
      });
      if (res.ok) {
        toast.success("Request sent to host!");
        setShowExtraInviteModal(false);
        setExtraInviteRequested(true);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to send request");
      }
    } catch (err) {
      toast.error("Failed to send request");
    } finally {
      setRequestingExtraInvites(false);
    }
  };

  // Remove member from VIP list
  const removeMember = async (memberId) => {
    if (!data?.vip_info?.list_id) return;
    try {
      const token = localStorage.getItem("session_token");
      // Include requester_rsvp_id for auth when not logged in
      const url = `${API}/events/${data.event.event_id}/vip-lists/${data.vip_info.list_id}/members/${memberId}?requester_rsvp_id=${rsvp?.rsvp_id || ''}`;
      const res = await fetch(url, {
        method: "DELETE",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.ok) {
        toast.success("Guest removed");
        fetchGuestListMembers();
      }
    } catch (err) {
      toast.error("Failed to remove guest");
    }
  };

  // Open manage guest list modal
  const openManageGuestList = () => {
    setShowManageGuestListModal(true);
    fetchGuestListMembers();
  };

  // Open edit preferences modal
  const openEditPreferences = () => {
    // Normalize preferences data to display format for consistent editing
    setEditingPreferences({
      dietary_restrictions: normalizePreferencesToDisplay(preferences?.dietary_restrictions || []),
      meat_preferences: normalizePreferencesToDisplay(preferences?.meat_preferences || []),
      allergies: normalizePreferencesToDisplay(preferences?.allergies || []),
      dietary_notes: preferences?.dietary_notes || "",
      accessibility_needs: normalizePreferencesToDisplay(preferences?.accessibility_needs || []),
      accessibility_notes: preferences?.accessibility_notes || "",
      invisible_disabilities: preferences?.invisible_disabilities || [],
      disability_notes: preferences?.disability_notes || preferences?.anything_else_to_share || "",
      interests: preferences?.interests || [],
      vibe: preferences?.vibe || "",
      headshot_url: preferences?.headshot_url || rsvp?.headshot_url || "",
      // About You fields from RSVP
      title: rsvp?.title || preferences?.title || "",
      company: rsvp?.company || preferences?.company || "",
      bio: rsvp?.bio || rsvp?.instructor_intake?.bio || preferences?.bio || "",
      linkedin: rsvp?.linkedin || preferences?.linkedin || "",
      instagram: rsvp?.instagram || preferences?.instagram || "",
      twitter: rsvp?.twitter || preferences?.twitter || "",
      website: rsvp?.website || preferences?.website || "",
      what_building: rsvp?.what_building || preferences?.what_building || "",
      stage: rsvp?.stage || preferences?.stage || "",
      project_link: rsvp?.project_link || preferences?.project_link || "",
    });
    setShowEditPreferencesModal(true);
  };

  // Get interest options based on event type
  const getEditInterestOptions = () => {
    const event = data?.event;
    if (!event) return ["Networking", "Learning", "Entertainment", "Food & Drinks", "Art", "Music", "Tech", "Fashion", "Wellness"];
    if (event.intake_settings?.interest_categories?.length > 0) {
      return event.intake_settings.interest_categories;
    }
    const pilotCategory = event.pilot_category?.toLowerCase();
    if (pilotCategory === 'instructor' || pilotCategory === 'workshop' || pilotCategory === 'builder') {
      return ["Product Development", "Fundraising & Investors", "Go-to-Market Strategy", "Engineering & Development", "Design & UX", "Community Building", "Content & Marketing", "Operations & Scaling", "AI & Automation", "Leadership & Team Building"];
    }
    return ["Networking", "Learning", "Entertainment", "Food & Drinks", "Art", "Music", "Tech", "Fashion", "Wellness"];
  };

  // Check if dietary should be shown
  const showDietaryInEdit = data?.event?.intake_settings?.collect_dietary !== false;
  const isInstructorEvent = data?.event?.pilot_category?.toLowerCase() === 'instructor';

  // Save edited preferences
  const savePreferences = async () => {
    setSavingPreferences(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/rsvps/${rsvpId}`, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(editingPreferences)
      });
      if (res.ok) {
        toast.success("Preferences updated!");
        // Refresh data
        const refreshRes = await fetch(`${API}/confirmation/${rsvpId}`);
        if (refreshRes.ok) {
          const refreshData = await refreshRes.json();
          setData(refreshData);
        }
        setShowEditPreferencesModal(false);
      } else {
        toast.error("Failed to update preferences");
      }
    } catch (err) {
      toast.error("Failed to save");
    } finally {
      setSavingPreferences(false);
    }
  };

  // Check if user is a VIP list host
  const isVipListHost = data?.rsvp?.is_vip_list_host || data?.rsvp?.label === 'host' || data?.rsvp?.label === 'team' || data?.vip_info?.role === 'list_host';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-gray-200 border-t-black rounded-full animate-spin" />
          <p className="text-gray-500 text-sm">Loading your confirmation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-6">
        <div className="text-center max-w-md">
          <PhygitalIcon className="w-12 h-12 mx-auto mb-4" variant="dark" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Confirmation Not Found</h1>
          <p className="text-gray-500 mb-6">{error}</p>
          <Link to="/">
            <Button variant="outline">Go Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const { rsvp, event, preferences, user, vip_info } = data;
  
  // Category preset colors from categoryConfig.js
  const catPreset = getCategoryPreset(event?.pilot_category || event?.category);
  const catBgColor = catPreset?.bgColor || "#ffffff";
  const catIconColor = catPreset?.iconColor || "#6b7280";
  const CatIcon = catPreset?.icon || null;

  // Dynamic theme from category preset (replaces hardcoded purple/violet)
  const theme = {
    bg: catBgColor,
    accent: catIconColor,
    accentLight: `${catIconColor}15`,
    accentMedium: `${catIconColor}30`,
    accentBorder: `${catIconColor}40`,
    gradient: `linear-gradient(135deg, ${catIconColor}, ${catIconColor}cc)`,
    gradientLight: `linear-gradient(135deg, ${catBgColor}, ${catIconColor}12)`,
  };
  
  // Get hosts from event - host_orgs are always hosts
  const hosts = [];
  if (event?.host_orgs && Array.isArray(event.host_orgs)) {
    hosts.push(...event.host_orgs);
  }
  if (event?.team && Array.isArray(event.team)) {
    const teamHosts = event.team.filter(m => m.role === 'host' || m.is_host || m.label === 'host');
    teamHosts.forEach(h => {
      if (!hosts.find(existing => existing.name === h.name || existing.name === h.org_name)) {
        hosts.push({ name: h.org_name || h.name, website: h.website });
      }
    });
  }

  // Get event logo (square) - use logo_image, fallback to cover_image
  const eventLogo = event?.logo_image 
    ? getImageUrl(event.logo_image) 
    : event?.cover_image 
      ? getImageUrl(event.cover_image) 
      : null;
  
  // Generate pass token from rsvpId
  const passToken = `PHY-${rsvpId.replace('rsvp_', '').toUpperCase().slice(0, 12)}`;

  return (
    <div 
      className="min-h-screen flex flex-col items-center justify-center font-sans overflow-hidden bg-white"
    >
      <PageMeta 
        title={event?.name ? `You're Confirmed - ${event.name}` : "Event Confirmation"}
        description={event?.name ? `Your RSVP for ${event.name} is confirmed` : "Your event confirmation"}
        image={event?.cover_image || event?.attendee_card_background}
      />
      {/* Main Content Card */}
      <div className="w-full max-w-md mx-auto px-4 py-8">
        {/* 5-stage banner: Booked → Soon → Almost time → Happening → Complete */}
        <ConfirmationStageBanner rsvp={rsvp} event={event} />
        
        {/* Host Dashboard Button - Only for hosts/cohosts */}
        {isEventHost && event?.event_id && (
          <div className="mb-4 flex justify-center">
            <button
              onClick={() => navigate(`/host/v2/${event.event_id}`)}
              className="flex items-center gap-2 px-4 py-2 bg-black hover:bg-gray-800 rounded-full text-sm text-white font-medium transition-colors"
              data-testid="confirmation-dashboard-btn"
            >
              <LayoutDashboard className="w-4 h-4" />
              Host Dashboard
            </button>
          </div>
        )}
        
        {/* Success Message - Clean and Simple */}
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm border"
            style={{ backgroundColor: `${catIconColor}15`, borderColor: `${catIconColor}30` }}>
            {CatIcon ? <CatIcon className="w-7 h-7" style={{ color: catIconColor }} /> : <Check className="w-7 h-7" style={{ color: catIconColor }} />}
          </div>
          
          <h2 className="text-xl font-semibold text-gray-900 mb-1" data-testid="confirmation-title">
            {pageContent?.title || "You're confirmed"}
          </h2>
          <p className="text-sm text-gray-500" data-testid="confirmation-subtitle">
            {pageContent?.subtitle || (event?.date ? (() => {
              const f = formatSafeDate(event.date, { weekday: "long", month: "long", day: "numeric" });
              return f ? `See you ${f}` : "See you soon";
            })() : "See you soon")}
          </p>
        </div>

        {/* Hero Custom Links (from admin/host configuration) */}
        {customLinks.filter(l => l.position === 'hero').length > 0 && (
          <div className="mb-6 space-y-2" data-testid="hero-custom-links">
            {customLinks.filter(l => l.position === 'hero').map((link, idx) => (
              <a
                key={link.id || idx}
                href={ensureAbsoluteUrl(link.url)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full p-3 text-white rounded-xl font-medium transition-all shadow-md"
                style={{ background: `linear-gradient(135deg, ${catIconColor}, ${catIconColor}dd)` }}
              >
                <Link2 className="w-4 h-4" />
                {link.label}
                <ExternalLink className="w-3 h-3 ml-1" />
              </a>
            ))}
          </div>
        )}

        {/* Digital Pass Card with Chrome Banner */}
        <div className="mb-6">
          <div 
            id="event-pass-card"
            className="bg-white rounded-3xl mb-4 shadow-xl border border-gray-200 overflow-hidden"
          >
            {/* Category-Themed Banner with Logo */}
            <div 
              className="relative h-24 flex items-center justify-between px-4"
              style={{
                background: `linear-gradient(135deg, ${catIconColor}25 0%, ${catIconColor}50 50%, ${catIconColor}25 100%)`
              }}
            >
              {/* Event Logo */}
              {eventLogo && (
                <div className="w-12 h-12 rounded-lg overflow-hidden bg-black/80 flex items-center justify-center shadow-lg">
                  <img 
                    src={eventLogo} 
                    alt={event?.name || "Event"} 
                    className="w-full h-full object-contain"
                  />
                </div>
              )}
              {/* VIP Badge if applicable */}
              {(rsvp?.is_vip || rsvp?.label === 'VIP' || vip_info?.is_vip) && (
                <span className="absolute top-3 right-3 text-[10px] bg-black/70 text-white px-2.5 py-1 rounded-full font-medium backdrop-blur-sm">
                  VIP
                </span>
              )}
              {/* Phygital watermark */}
              <div className="absolute bottom-2 right-3 flex items-center gap-1 text-white/60">
                <PhygitalIcon className="w-3 h-3" />
                <span className="text-[9px] font-medium">phygital</span>
              </div>
            </div>

            {/* Pass Content */}
            <div className="p-5">
              {/* Pass Header */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Event Pass</p>
                  <p className="text-gray-900 font-semibold">{event?.name || "Event"}</p>
                </div>
                {/* Dynamic status badge based on attendee_type */}
                {(() => {
                  const attendeeType = (rsvp?.attendee_type || 'attendee').toLowerCase();
                  const badgeConfig = {
                    'vip': { text: 'VIP', bg: 'bg-amber-100', fg: 'text-amber-700' },
                    'host': { text: 'HOST', bg: 'bg-blue-100', fg: 'text-blue-700' },
                    'list_host': { text: 'HOST', bg: 'bg-blue-100', fg: 'text-blue-700' },
                    'partner': { text: 'PARTNER', bg: 'bg-purple-100', fg: 'text-purple-700' },
                    'team': { text: 'TEAM', bg: 'bg-pink-100', fg: 'text-pink-700' },
                    'staff': { text: 'TEAM', bg: 'bg-pink-100', fg: 'text-pink-700' },
                  };
                  const config = badgeConfig[attendeeType] || { text: 'Confirmed', bg: 'bg-green-100', fg: 'text-green-700' };
                  return (
                    <span className={`text-[10px] ${config.bg} ${config.fg} px-2.5 py-1 rounded-full font-medium`}>
                      {config.text}
                    </span>
                  );
                })()}
              </div>
              
              {/* Event Info Grid */}
              <div className="bg-gray-50 rounded-2xl p-4 mb-4">
                {/* Demo Account Badge */}
                {rsvp?.is_demo_account && (
                  <div className="flex justify-center mb-3">
                    <span className="px-3 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded-full border border-orange-200">
                      {rsvp?.demo_label || "Demo Account"}
                    </span>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase font-medium mb-0.5">Guest</p>
                    <p className="text-gray-900 font-medium">{rsvp?.name || `${rsvp?.first_name || ''} ${rsvp?.last_name || ''}`.trim() || "Guest"}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase font-medium mb-0.5">Date</p>
                    <p className="text-gray-900 font-medium">
                      {formatSafeDate(event?.date) || "TBA"}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase font-medium mb-0.5">Time</p>
                    <p className="text-gray-900 font-medium">
                      {event?.start_time ? formatTime12Hour(event.start_time, event?.timezone ? getTimezoneAbbr(event.timezone) : '') : event?.time ? formatTime12Hour(event.time, event?.timezone ? getTimezoneAbbr(event.timezone) : '') : "TBA"}
                      {event?.end_time && ` - ${formatTime12Hour(event.end_time)}`}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase font-medium mb-0.5">
                      {event?.location_type === 'virtual' || event?.is_virtual ? 'Join' : 'Location'}
                    </p>
                    <p className="text-gray-900 font-medium text-xs">
                      {event?.location_type === 'virtual' || event?.is_virtual
                        ? (event?.zoom_link ? 'Virtual (Zoom)' : 'Virtual')
                        : (event?.location || event?.city || "TBA")}
                    </p>
                    {event?.zoom_link && (
                      <a href={event.zoom_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-[10px] hover:underline">
                        Join Zoom Link
                      </a>
                    )}
                  </div>
                </div>
                {/* Location Week Of Notice - Full width under Time/Venue */}
                {event?.location_after_rsvp && (
                  <p className="text-[10px] text-gray-500 mt-2 flex items-center justify-center gap-1">
                    <Mail className="w-3 h-3 flex-shrink-0" />
                    Location details will be sent via email the week of the event
                  </p>
                )}
              </div>
              
              {/* QR Code */}
              <div className="flex justify-center mb-3">
                <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm">
                  <img 
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(window.location.href)}`}
                    alt="Event Pass QR Code" 
                    className="w-28 h-28"
                  />
                </div>
              </div>
              <p className="text-gray-400 font-mono text-[10px] text-center mb-2">{passToken}</p>
              
              {/* Email notification under QR */}
              {rsvp?.email && (
                <p className="text-xs text-gray-500 text-center">
                  Confirmation sent to <span className="font-medium">{rsvp.email}</span>
                </p>
              )}
            </div>
          </div>
          
          <p className="text-xs text-gray-500 text-center mb-5">
            Show this pass at entry
          </p>
          
          {/* Learn More About Event Link - Only show if host explicitly sets an external URL or enables it */}
          {event?.external_website_url && event.external_website_url.startsWith("http") && (
          <div className="text-center mb-4">
            <a 
              href={event.external_website_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-purple-600 hover:text-purple-700 transition-colors"
              data-testid="learn-more-event-link"
            >
              <ExternalLink className="w-4 h-4" />
              {event?.learn_more_text || "Learn more about this event"}
            </a>
          </div>
          )}
          
          {/* Add to Apple Wallet Button - Hidden until tested (controlled by event.show_apple_wallet) */}
          {event?.show_apple_wallet && (
          <div className="mb-4">
            <button
              onClick={async () => {
                const walletUrl = `${API}/events/${event?.event_id}/rsvps/${rsvp?.rsvp_id}/wallet-pass`;
                const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
                
                if (isIOS) {
                  // For iOS Safari, navigate directly - iOS handles .pkpass files natively
                  window.location.href = walletUrl;
                } else if (isSafari) {
                  // For desktop Safari, use fetch and create blob
                  try {
                    const response = await fetch(walletUrl);
                    if (!response.ok) throw new Error('Download failed');
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${event?.name || 'Event'}_Pass.pkpass`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                  } catch (err) {
                    console.error('Download error:', err);
                    window.open(walletUrl, '_blank');
                  }
                } else {
                  // For Chrome/Firefox, just open the URL
                  window.location.href = walletUrl;
                }
              }}
              className="flex items-center justify-center w-full"
              data-testid="add-to-wallet-btn"
            >
              <img 
                src="/assets/images/add-to-apple-wallet.svg"
                alt="Add to Apple Wallet"
                className="h-11 w-auto hover:opacity-80 transition-opacity cursor-pointer"
                style={{ minWidth: '140px' }}
              />
            </button>
          </div>
          )}

          {/* Action Buttons - Side by Side - Controlled by event display_settings */}
          {event?.display_settings?.show_confirmation_buttons !== false && (
          <div className="grid grid-cols-2 gap-3 mb-4">
            <Button 
              onClick={copyPassLink}
              variant="outline"
              className="text-xs py-2.5 px-4 rounded-xl border-gray-200 hover:bg-gray-50 bg-white"
              data-testid="share-link-btn"
            >
              {linkCopied ? <Check className="w-3.5 h-3.5 mr-1.5 text-green-600" /> : <ArrowUp className="w-3.5 h-3.5 mr-1.5" />}
              {linkCopied ? "Copied!" : "Share Link"}
            </Button>
            
            <div className="relative">
              <Button 
                onClick={() => setShowCalendarOptions(!showCalendarOptions)}
                variant="outline"
                className="w-full border-gray-200 py-2.5 px-4 rounded-xl text-xs hover:bg-gray-50 bg-white"
                data-testid="add-to-calendar-btn"
              >
                <CalendarPlus className="w-3.5 h-3.5 mr-1.5" />
                Add to Calendar
              </Button>
              
              {showCalendarOptions && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden z-10">
                  <a 
                    href={generateCalendarUrl('google')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-sm"
                  >
                    <Calendar className="w-4 h-4 text-gray-500" />
                    Google Calendar
                  </a>
                  <a 
                    href={generateCalendarUrl('apple')}
                    download={`${event?.slug || 'event'}.ics`}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-sm border-t"
                  >
                    <Calendar className="w-4 h-4 text-gray-500" />
                    Apple Calendar
                  </a>
                </div>
              )}
            </div>
          </div>
          )}

          {/* For VIP List Hosts - Guest Invite Link */}
          {isVipListHost && (
            <div className="mb-4 p-4 rounded-xl border" style={{ backgroundColor: theme.accentLight, borderColor: theme.accentBorder }}>
              <h4 className="text-sm font-medium mb-2 flex items-center gap-2" style={{ color: catIconColor }}>
                <Users className="w-4 h-4" />
                Share with Your Guests
              </h4>
              <p className="text-xs mb-3" style={{ color: `${catIconColor}cc` }}>Copy this link to invite your guests to RSVP</p>
              <Button 
                onClick={copyGuestInviteLink}
                variant="outline"
                className="w-full text-xs py-2.5 rounded-xl bg-white"
                style={{ borderColor: theme.accentBorder }}
                data-testid="copy-guest-invite-btn"
              >
                {guestInviteLinkCopied ? <Check className="w-3.5 h-3.5 mr-1.5 text-green-600" /> : <Link2 className="w-3.5 h-3.5 mr-1.5" />}
                {guestInviteLinkCopied ? "Copied!" : "Copy Guest Invite Link"}
              </Button>
            </div>
          )}

          {/* Pre-service link cards - Iter 9.50+ R14
              Each card: image thumbnail + title + subtitle + external link icon.
              Pulled from event.confirmation_settings.pre_links so the host can
              attach polished prep links (day-of prep guide, what to wear, inspo
              board, deposit link) without code. */}
          {(event?.confirmation_settings?.pre_links || []).length > 0 && (
            <div className="mb-4 p-4 rounded-xl border border-amber-100 bg-amber-50" data-testid="confirm-pre-block">
              <h4 className="text-[10px] uppercase tracking-wider text-amber-700 font-bold mb-3">Before your appointment</h4>
              {event?.confirmation_settings?.pre_text && (
                <p className="text-sm text-stone-700 whitespace-pre-line mb-3">{event.confirmation_settings.pre_text}</p>
              )}
              <div className="space-y-2" data-testid="confirm-pre-links">
                {(event.confirmation_settings.pre_links || []).map((lnk, i) => (
                  <a
                    key={i}
                    href={lnk.url || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-2.5 rounded-xl bg-white border border-amber-100 hover:border-amber-300 hover:shadow-sm transition-all"
                    data-testid={`confirm-pre-link-${i}`}
                  >
                    <div className="w-12 h-12 rounded-lg bg-amber-100 overflow-hidden flex-shrink-0">
                      {lnk.image_url && <img src={lnk.image_url} alt={lnk.title} className="w-full h-full object-cover" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-stone-900 truncate">{lnk.title || "Open link"}</p>
                      {lnk.subtitle && <p className="text-[11px] text-stone-500 truncate">{lnk.subtitle}</p>}
                    </div>
                    <ExternalLink className="w-4 h-4 text-stone-400 flex-shrink-0" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Post-service link cards - Iter 9.50+ R14
              Aftercare, rebook link, leave a review, share your look, etc. */}
          {(event?.confirmation_settings?.post_links || []).length > 0 && (
            <div className="mb-4 p-4 rounded-xl border border-emerald-100 bg-emerald-50" data-testid="confirm-post-block">
              <h4 className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold mb-3">After your appointment</h4>
              {event?.confirmation_settings?.post_text && (
                <p className="text-sm text-stone-700 whitespace-pre-line mb-3">{event.confirmation_settings.post_text}</p>
              )}
              <div className="space-y-2" data-testid="confirm-post-links">
                {(event.confirmation_settings.post_links || []).map((lnk, i) => (
                  <a
                    key={i}
                    href={lnk.url || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-2.5 rounded-xl bg-white border border-emerald-100 hover:border-emerald-300 hover:shadow-sm transition-all"
                    data-testid={`confirm-post-link-${i}`}
                  >
                    <div className="w-12 h-12 rounded-lg bg-emerald-100 overflow-hidden flex-shrink-0">
                      {lnk.image_url && <img src={lnk.image_url} alt={lnk.title} className="w-full h-full object-cover" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-stone-900 truncate">{lnk.title || "Open link"}</p>
                      {lnk.subtitle && <p className="text-[11px] text-stone-500 truncate">{lnk.subtitle}</p>}
                    </div>
                    <ExternalLink className="w-4 h-4 text-stone-400 flex-shrink-0" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* RSVP Management Buttons - Only show if status is not cancelled */}
          {/* REMOVED: Edit Preferences + Can't Make It moved to below Your Preferences section */}

          {/* Show cancelled status if RSVP was cancelled */}
          {rsvp?.status === "cant_make_it" && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 text-center">
              <XCircle className="w-6 h-6 text-red-500 mx-auto mb-2" />
              <p className="text-sm font-medium text-red-700">RSVP Cancelled</p>
              <p className="text-xs text-red-500 mt-1">You've indicated you can't make it to this event.</p>
            </div>
          )}

          {/* SELF CHECK-IN + CAN'T MAKE IT + RESOURCES  - Moved below Your Preferences */}
        </div>

        {/* Instructor Info Section - For audience members of instructor events (Master Plan §9) */}
        {isInstructorEvent && !(rsvp?.label === 'instructor' || rsvp?.is_instructor || rsvp?.tags?.includes('instructor')) && event?.instructor && (
          <div className="mb-6" data-testid="audience-instructor-section">
            <div className="rounded-2xl p-5 border" style={{ background: theme.gradientLight, borderColor: theme.accentBorder }}>
              <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">Instructor</h3>
              <div className="flex items-center gap-4">
                {(event.instructor.headshot_url || event.instructor.headshot) && (
                  <img 
                    src={event.instructor.headshot_url || event.instructor.headshot} 
                    alt={event.instructor.name}
                    className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-md"
                    onError={e => e.target.style.display='none'}
                  />
                )}
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">{event.instructor.name}</p>
                  {event.instructor.session_title && (
                    <p className="text-sm text-gray-600 mt-0.5">{event.instructor.session_title}</p>
                  )}
                  {event.instructor.topics && event.instructor.topics.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {event.instructor.topics.slice(0, 5).map((topic, idx) => (
                        <span key={idx} className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{topic}</span>
                      ))}
                    </div>
                  )}
                  {event.instructor.linkedin && (
                    <a href={event.instructor.linkedin.startsWith('http') ? event.instructor.linkedin : `https://linkedin.com/in/${event.instructor.linkedin}`} 
                       target="_blank" rel="noopener noreferrer"
                       className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 mt-2" data-testid="instructor-linkedin">
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452z" /></svg>
                      LinkedIn
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Speaker Intake Card - Show for speakers */}
        {(rsvp?.label === 'speaker' || rsvp?.is_speaker || rsvp?.tags?.includes('speaker')) && (
          <div className="mb-6" data-testid="speaker-intake-section">
            <SpeakerIntakeCard
              rsvpId={rsvpId}
              eventId={event?.event_id}
              initialData={{
                name: rsvp?.name,
                title: rsvp?.title,
                company: rsvp?.company,
                ...rsvp?.speaker_intake,
                speaker_intake_completed: rsvp?.speaker_intake_completed
              }}
              isExpanded={!rsvp?.speaker_intake_completed}
              readOnly={false}  // Allow speakers to edit their info anytime
            />
          </div>
        )}

        {/* Instructor Section - Show for instructors (like speakers but for instructor pilots) */}
        {(rsvp?.label === 'instructor' || rsvp?.is_instructor || rsvp?.tags?.includes('instructor')) && (
          <div className="mb-6" data-testid="instructor-section">
            <div className="rounded-2xl p-5 border" style={{ background: theme.gradientLight, borderColor: theme.accentBorder }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {pageContent?.instructor_section?.title || "Instructor Dashboard"}
                  </h3>
                  <p className="text-xs text-gray-500">
                    {data?.event?.instructor_confirmation_message || pageContent?.instructor_section?.subtitle || "Thanks for submitting! We're setting up the event and will share updates along the way."}
                  </p>
                </div>
              </div>
              
              {/* Zoom Link - if available */}
              {(event?.zoom_link || event?.virtual_link) && (
                <div className="mb-4 p-3 bg-white rounded-xl border border-indigo-100">
                  <div className="flex items-center gap-2 mb-2">
                    <ExternalLink className="w-4 h-4 text-indigo-500" />
                    <span className="text-sm font-medium text-gray-700">
                      {pageContent?.instructor_section?.session_link_label || "Session Link"}
                    </span>
                  </div>
                  <a 
                    href={event?.zoom_link || event?.virtual_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-indigo-600 hover:text-indigo-700 underline break-all"
                  >
                    {event?.zoom_link || event?.virtual_link}
                  </a>
                </div>
              )}

              {/* Your Completed Intake Summary */}
              {rsvp?.instructor_intake_completed && (
                <div className="p-3 bg-green-50 rounded-xl border border-green-200 mb-4">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium text-green-700">
                      {pageContent?.instructor_section?.intake_completed_label || "Intake Completed"}
                    </span>
                  </div>
                  {rsvp?.instructor_intake && (
                    <div className="mt-2 text-xs text-gray-600">
                      {rsvp.instructor_intake.bio && <p className="truncate">Bio: {rsvp.instructor_intake.bio}</p>}
                      {rsvp.instructor_intake.expertise && <p className="truncate">Expertise: {rsvp.instructor_intake.expertise}</p>}
                    </div>
                  )}
                </div>
              )}
              
              {/* Audience Insights Link  - for instructor only */}
              {(rsvp?.label === 'instructor' || rsvp?.is_instructor || rsvp?.tags?.includes('instructor')) && (
                <a
                  href={`/events/${event?.event_id}/share/instructor`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block mt-3 p-3 bg-indigo-50 rounded-xl border border-indigo-200 hover:bg-indigo-100 transition-colors"
                  data-testid="audience-insights-link"
                >
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-indigo-600" />
                    <span className="text-sm font-medium text-indigo-700">View Audience Insights</span>
                    <ExternalLink className="w-3.5 h-3.5 text-indigo-400 ml-auto" />
                  </div>
                  <p className="text-xs text-indigo-500 mt-1">See who's attending, their interests, and dietary needs</p>
                </a>
              )}
            </div>
          </div>
        )}

        {/* ===== RESOURCES SECTION (For Instructor Pilots Only) ===== */}
        {event?.event_type === 'instructor_pilot' && (
          <div className="mb-6" data-testid="resources-section">
            {/* Resources are visible after check-in OR if the event doesn't require check-in for content */}
            {(isCheckedIn || !event?.resources_config?.visible_after_checkin) && (
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-teal-50">
                  <div className="flex items-center gap-2">
                    <ExternalLink className="w-5 h-5 text-emerald-600" />
                    <h3 className="font-semibold text-gray-900">
                      {pageContent?.resources_section?.title || "Resources"}
                    </h3>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {pageContent?.resources_section?.subtitle || "Materials and links for this session"}
                  </p>
                </div>
                
                <div className="p-4 space-y-3">
                  {/* Custom Links from Admin/Host */}
                  {customLinks.filter(l => l.position === 'resources').length > 0 && (
                    <div>
                      <p className="text-xs font-medium mb-2 uppercase tracking-wide" style={{ color: catIconColor }}>Quick Links</p>
                      {customLinks.filter(l => l.position === 'resources').map((link, idx) => (
                        <a
                          key={link.id || idx}
                          href={ensureAbsoluteUrl(link.url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 p-2 rounded-lg transition-colors mb-1"
                          style={{ color: catIconColor }}
                          onMouseOver={e => e.currentTarget.style.backgroundColor = theme.accentLight}
                          onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <Link2 className="w-4 h-4" style={{ color: catIconColor }} />
                          <span className="text-sm text-gray-700">{link.label}</span>
                          <ExternalLink className="w-3 h-3 text-gray-400 ml-auto" />
                        </a>
                      ))}
                    </div>
                  )}
                  
                  {/* Instructor Resources */}
                  {event?.instructor_resources?.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-indigo-600 mb-2 uppercase tracking-wide">
                        {pageContent?.resources_section?.from_instructor_label || "From Instructor"}
                      </p>
                      {event.instructor_resources.map((res, idx) => (
                        <a
                          key={idx}
                          href={res.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-3 bg-indigo-50 rounded-xl border border-indigo-100 hover:bg-indigo-100 transition-colors mb-2"
                        >
                          <div className="p-2 bg-indigo-100 rounded-lg">
                            <ExternalLink className="w-4 h-4 text-indigo-600" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-sm text-gray-900">{res.title}</p>
                            {res.description && <p className="text-xs text-gray-500">{res.description}</p>}
                          </div>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </a>
                      ))}
                    </div>
                  )}

                  {/* Organization Resources */}
                  {event?.org_resources?.length > 0 && (
                    <div>
                      <p className="text-xs font-medium mb-2 uppercase tracking-wide" style={{ color: catIconColor }}>
                        {pageContent?.resources_section?.from_organization_label || `From ${event?.host_org?.name || 'Organization'}`}
                      </p>
                      {event.org_resources.map((res, idx) => (
                        <a
                          key={idx}
                          href={res.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-3 rounded-xl border transition-colors mb-2"
                          style={{ backgroundColor: theme.accentLight, borderColor: theme.accentBorder }}
                        >
                          <div className="p-2 rounded-lg" style={{ backgroundColor: theme.accentMedium }}>
                            <ExternalLink className="w-4 h-4" style={{ color: catIconColor }} />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-sm text-gray-900">{res.title}</p>
                            {res.description && <p className="text-xs text-gray-500">{res.description}</p>}
                          </div>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </a>
                      ))}
                    </div>
                  )}

                  {/* General Resources from resources_config */}
                  {event?.resources_config?.resources?.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-emerald-600 mb-2 uppercase tracking-wide">
                        {pageContent?.resources_section?.session_materials_label || "Session Materials"}
                      </p>
                      {event.resources_config.resources.filter(r => r.title && r.url).map((res, idx) => (
                        <a
                          key={idx}
                          href={res.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-3 bg-emerald-50 rounded-xl border border-emerald-100 hover:bg-emerald-100 transition-colors mb-2"
                        >
                          <div className="p-2 bg-emerald-100 rounded-lg">
                            <ExternalLink className="w-4 h-4 text-emerald-600" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-sm text-gray-900">{res.title}</p>
                            {res.description && <p className="text-xs text-gray-500">{res.description}</p>}
                          </div>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </a>
                      ))}
                    </div>
                  )}

                  {/* Host CTA Button */}
                  {event?.resources_config?.cta_button && (
                    <div className="pt-3 border-t">
                      <a
                        href={event.resources_config.cta_button.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-white rounded-xl font-medium transition-all"
                        style={{ background: theme.gradient }}
                      >
                        {event.resources_config.cta_button.text || 'Learn More'}
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  )}

                  {/* Empty state */}
                  {!event?.instructor_resources?.length && 
                   !event?.org_resources?.length && 
                   !event?.resources_config?.resources?.length && (
                    <div className="text-center py-6 text-gray-400">
                      <ExternalLink className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No resources available yet</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Resources locked - need to check in (not shown to instructors) */}
            {!isCheckedIn && event?.resources_config?.visible_after_checkin && !(rsvp?.label === 'instructor' || rsvp?.is_instructor || rsvp?.tags?.includes('instructor')) && (
              <div className="p-4 bg-gray-100 rounded-2xl border border-gray-200 relative overflow-hidden">
                <div className="absolute inset-0 backdrop-blur-sm bg-white/70 flex items-center justify-center z-10">
                  <div className="text-center">
                    <Lock className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-600">Check in to unlock resources</p>
                  </div>
                </div>
                <div className="opacity-30">
                  <div className="flex items-center gap-3 p-3 bg-gray-200 rounded-lg">
                    <div className="w-8 h-8 bg-gray-300 rounded"></div>
                    <div className="flex-1">
                      <div className="h-3 bg-gray-300 rounded w-3/4 mb-1"></div>
                      <div className="h-2 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Instructor: Resources coming soon (until host approves) */}
            {(rsvp?.label === 'instructor' || rsvp?.is_instructor || rsvp?.tags?.includes('instructor')) && 
             !event?.instructor_resources?.length && 
             !event?.resources_config?.resources?.length && (
              <div className="p-4 bg-amber-50 rounded-2xl border border-amber-200">
                <div className="text-center py-3">
                  <Clock className="w-6 h-6 text-amber-500 mx-auto mb-2" />
                  <p className="text-sm font-medium text-amber-700">Resources coming soon</p>
                  <p className="text-xs text-amber-500 mt-1">The host will review and add your submitted resources here once approved.</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ===== POST-SURVEY SECTION (For Instructor Pilots Only) ===== */}
        {event?.event_type === 'instructor_pilot' && 
         event?.post_survey_config?.enabled && 
         event?.post_survey_config?.show_on_confirmation && (
          <div className="mb-6" data-testid="post-survey-section">
            <div className="rounded-2xl p-5 border" style={{ background: theme.gradientLight, borderColor: theme.accentBorder }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: theme.accentLight }}>
                  <MessageSquare className="w-5 h-5" style={{ color: catIconColor }} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Share Your Feedback</h3>
                  <p className="text-xs text-gray-500">Help us improve future sessions</p>
                </div>
              </div>
              
              {/* Survey Questions */}
              {event?.post_survey_config?.questions?.length > 0 ? (
                <div className="space-y-4">
                  {event.post_survey_config.questions.map((q, idx) => (
                    <div key={q.id || idx} className="bg-white p-4 rounded-xl border border-gray-100">
                      <Label className="text-sm font-medium text-gray-700 mb-2 block">
                        {q.question}
                        {q.required && <span className="text-red-500 ml-1">*</span>}
                      </Label>
                      
                      {q.type === 'rating' && (
                        <div className="flex gap-2 mt-2">
                          {[1, 2, 3, 4, 5].map(n => (
                            <button
                              key={n}
                              className="w-10 h-10 rounded-lg border-2 border-gray-200 hover:border-violet-400 hover:bg-violet-50 text-sm font-medium transition-all"
                              data-testid={`survey-rating-${q.id}-${n}`}
                            >
                              {n}
                            </button>
                          ))}
                        </div>
                      )}
                      
                      {q.type === 'nps' && (
                        <div className="flex gap-1 mt-2 flex-wrap">
                          {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                            <button
                              key={n}
                              className="w-8 h-8 rounded-lg border border-gray-200 hover:border-violet-400 hover:bg-violet-50 text-xs font-medium transition-all"
                              data-testid={`survey-nps-${q.id}-${n}`}
                            >
                              {n}
                            </button>
                          ))}
                        </div>
                      )}
                      
                      {q.type === 'text' && (
                        <textarea
                          className="w-full mt-2 p-3 border border-gray-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-violet-400 focus:border-transparent"
                          rows={3}
                          placeholder="Your response..."
                          data-testid={`survey-text-${q.id}`}
                        />
                      )}
                      
                      {q.type === 'yesno' && (
                        <div className="flex gap-3 mt-2">
                          <button className="flex-1 py-2 rounded-lg border-2 border-gray-200 hover:border-green-400 hover:bg-green-50 text-sm font-medium transition-all">
                            Yes
                          </button>
                          <button className="flex-1 py-2 rounded-lg border-2 border-gray-200 hover:border-red-400 hover:bg-red-50 text-sm font-medium transition-all">
                            No
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                  
                  <Button className="w-full text-white" style={{ backgroundColor: catIconColor }}>
                    Submit Feedback
                  </Button>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-sm text-gray-500">Survey will be available after the session</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════ */}
        {/* EVENT PHASE INDICATOR - Pre / During / Post */}
        {/* ═══════════════════════════════════════════════════ */}
        {(() => {
          const eventDate = event?.date ? new Date(event.date) : null;
          const now = new Date();
          const phase = !eventDate ? "pre" : now < eventDate ? "pre" : 
            (now - eventDate < 24 * 60 * 60 * 1000) ? "during" : "post";
          const phaseConfig = {
            pre:    { label: slotText(cnf, "phase_pre_label", "Before Your Appointment"),
                       color: "bg-blue-50 border-blue-200 text-blue-700", icon: "clock" },
            during: { label: slotText(cnf, "phase_during_label", "Happening Now"),
                       color: "bg-green-50 border-green-200 text-green-700", icon: "check" },
            post:   { label: slotText(cnf, "phase_post_label", "Completed"),
                       color: "bg-gray-50 border-gray-200 text-gray-600", icon: "archive" },
          };
          const pc = phaseConfig[phase];
          return (
            <div className={`flex items-center justify-center gap-2 px-4 py-2 rounded-full border text-xs font-medium mb-4 ${pc.color}`}
              data-testid={`event-phase-${phase}`}>
              {phase === "pre" && <Clock className="w-3.5 h-3.5" />}
              {phase === "during" && <CheckCircle2 className="w-3.5 h-3.5" />}
              {phase === "post" && <Archive className="w-3.5 h-3.5" />}
              {pc.label}
            </div>
          );
        })()}

        {/* ═══════════════════════════════════════════════════ */}
        {/* YOUR PREFERENCES - Inline display with Edit button */}
        {/* ═══════════════════════════════════════════════════ */}
        <div className="mb-4 bg-white rounded-2xl border border-gray-200 overflow-hidden" data-testid="your-preferences-section">
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4" style={{ color: catIconColor }} />
              <span className="text-sm font-semibold text-gray-900">Your Preferences</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowPreferenceCard(true)}
                className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded-lg hover:bg-gray-50 transition-colors"
                data-testid="view-preference-card-btn"
              >
                Full Card
              </button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setEditingPreferences({
                    dietary_restrictions: preferences?.dietary_restrictions || [],
                    meat_preferences: preferences?.meat_preferences || [],
                    allergies: preferences?.allergies || [],
                    dietary_notes: preferences?.dietary_notes || "",
                    accessibility_needs: preferences?.accessibility_needs || [],
                    accessibility_notes: preferences?.accessibility_notes || "",
                    invisible_disabilities: preferences?.invisible_disabilities || [],
                    vibe: preferences?.vibe || "",
                    interests: preferences?.interests || [],
                    headshot_url: rsvp?.headshot_url || "",
                  });
                  setShowEditPreferencesModal(true);
                }}
                className="text-xs h-7 gap-1"
                data-testid="edit-preferences-btn"
              >
                <Edit3 className="w-3 h-3" />
                Edit
              </Button>
            </div>
          </div>
          <div className="p-4 space-y-3">
            {/* RSVP-specific booking preferences (prominent) */}
            {rsvp?.preferences && Object.keys(rsvp.preferences).length > 0 && (
              <div className="space-y-1.5" data-testid="booking-preferences">
                <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: catIconColor }}>Booking Details</span>
                <div className="grid grid-cols-2 gap-1.5">
                  {Object.entries(rsvp.preferences).filter(([k, v]) => v && String(v).trim()).map(([key, value]) => {
                    const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                    return (
                      <div key={key} className="p-2 rounded-lg" style={{ backgroundColor: `${catIconColor}08` }}>
                        <span className="text-[9px] font-medium uppercase tracking-wide text-gray-400">{label}</span>
                        <p className="text-xs font-medium text-gray-800 mt-0.5">{String(value)}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Platform preferences (dietary/accessibility - only when relevant) */}
            {preferences && (() => {
              const isBeauty = ['makeup', 'hair', 'nail', 'lash'].includes(event?.pilot_category);
              return Object.entries(preferences).filter(([k, v]) => {
                if (!v) return false;
                if (Array.isArray(v) && v.length === 0) return false;
                if (typeof v === 'string' && !v.trim()) return false;
                if (k.startsWith('_') || k === 'shared_defaults' || k === 'headshot_url' || k === 'tap_in_song' || k === 'experience_apps' || k === 'vibe_preferences') return false;
                // Skip dietary for beauty categories
                if (isBeauty && (k === 'dietary_restrictions' || k === 'meat_preferences' || k === 'dietary_notes')) return false;
                return true;
              });
            })().length > 0 && (
              <div className="grid grid-cols-1 gap-2">
                {(() => {
                  const isBeauty = ['makeup', 'hair', 'nail', 'lash'].includes(event?.pilot_category);
                  return Object.entries(preferences).filter(([k, v]) => {
                    if (!v) return false;
                    if (Array.isArray(v) && v.length === 0) return false;
                    if (typeof v === 'string' && !v.trim()) return false;
                    if (k.startsWith('_') || k === 'shared_defaults' || k === 'headshot_url' || k === 'tap_in_song' || k === 'experience_apps' || k === 'vibe_preferences') return false;
                    if (isBeauty && (k === 'dietary_restrictions' || k === 'meat_preferences' || k === 'dietary_notes')) return false;
                    return true;
                  }).map(([key, value]) => {
                    const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                    const colorMap = {
                      dietary_restrictions: "bg-green-50 text-green-700",
                      allergies: "bg-red-50 text-red-700",
                      accessibility_needs: "bg-blue-50 text-blue-700",
                      meat_preferences: "bg-amber-50 text-amber-700",
                      vibe: "bg-pink-50 text-pink-700",
                      interests: "bg-purple-50 text-purple-700",
                    };
                    const colors = colorMap[key] || "bg-gray-50 text-gray-700";
                    return (
                      <div key={key} className={`p-2.5 rounded-lg ${colors.split(' ')[0]}`}>
                        <span className={`text-[10px] font-medium uppercase tracking-wide ${colors.split(' ')[1]} opacity-70`}>{label}</span>
                        <div className="mt-1">
                          {Array.isArray(value) ? (
                            <div className="flex flex-wrap gap-1">
                              {value.map((item, i) => (
                                <Badge key={i} variant="outline" className="text-xs bg-white/80">{String(item)}</Badge>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs font-medium">{String(value)}</span>
                          )}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            )}

            {/* Linked Experiences Preview */}
            {(rsvp?.linked_experiences?.length > 0 || rsvp?.linked_products?.length > 0) && (
              <div className="border-t border-gray-100 pt-3 space-y-2">
                {rsvp?.linked_experiences?.length > 0 && (
                  <div>
                    <span className="text-[10px] font-medium uppercase tracking-wide" style={{ color: catIconColor }}>Linked Experiences</span>
                    <div className="mt-1 space-y-1.5">
                      {rsvp.linked_experiences.map((exp, i) => (
                        <div key={i} className="flex items-center gap-2 p-2 rounded-lg" style={{ backgroundColor: `${catIconColor}08` }}>
                          {exp.image && <img src={exp.image} alt="" className="w-8 h-8 rounded object-cover shrink-0" />}
                          <span className="text-xs font-medium text-gray-800">{exp.name || exp.title || `Experience ${i + 1}`}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {rsvp?.linked_products?.length > 0 && (
                  <div>
                    <span className="text-[10px] font-medium uppercase tracking-wide" style={{ color: catIconColor }}>Add-Ons / Products</span>
                    <div className="mt-1 space-y-1.5">
                      {rsvp.linked_products.map((prod, i) => (
                        <div key={i} className="flex items-center gap-2 p-2 rounded-lg" style={{ backgroundColor: `${catIconColor}08` }}>
                          {prod.image && <img src={prod.image} alt="" className="w-8 h-8 rounded object-cover shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <span className="text-xs font-medium text-gray-800">{prod.name || prod}</span>
                            {prod.price && <span className="text-[10px] text-gray-500 ml-1">${prod.price}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Service Info */}
            {rsvp?.service_name && (
              <div className="border-t border-gray-100 pt-3">
                <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">Service</span>
                <p className="text-sm font-medium text-gray-900 mt-0.5">{rsvp.service_name}</p>
                {rsvp?.appointment_date && (
                  <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(rsvp.appointment_date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                    {rsvp.appointment_time && ` at ${rsvp.appointment_time}`}
                  </p>
                )}
              </div>
            )}

            {/* Inspo Images Preview */}
            {(rsvp?.inspo_images?.length > 0 || rsvp?.inspiration_images?.length > 0) && (
              <div className="border-t border-gray-100 pt-3">
                <span className="text-[10px] font-medium uppercase tracking-wide" style={{ color: catIconColor }}>Your Inspiration</span>
                <div className="mt-1.5 flex gap-1.5 overflow-x-auto pb-1">
                  {(rsvp.inspo_images || rsvp.inspiration_images || []).slice(0, 6).map((img, i) => {
                    const src = typeof img === 'string' ? img : (img.url || img.image_url);
                    return (
                      <img
                        key={i}
                        src={src}
                        alt=""
                        className="w-14 h-14 rounded-lg object-cover shrink-0 cursor-pointer hover:ring-2 transition-all"
                        style={{ '--tw-ring-color': catIconColor }}
                        onClick={() => setExpandedImage(src)}
                        data-testid={`inspo-image-${i}`}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════ */}
        {/* SERVICE UPSELLS - Linked classes, products, experiences */}
        {/* ═══════════════════════════════════════════════════ */}
        {serviceUpsells.length > 0 && (
          <div className="mb-4 bg-white rounded-2xl border border-gray-200 overflow-hidden" data-testid="service-upsells-section">
            <div className="flex items-center gap-2 p-4 border-b border-gray-100">
              <ShoppingBag className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-semibold text-gray-900">Add to Your Experience</span>
            </div>
            <div className="p-3 space-y-2">
              {serviceUpsells.map(upsell => {
                const isSelected = rsvp?.selected_upsells?.some(s => s.upsell_id === upsell.upsell_id);
                const isLinked = !!upsell.linked_dashboard_id;
                return (
                  <div key={upsell.upsell_id}
                    className={`p-3 rounded-xl border transition-all ${isSelected ? "border-green-300 bg-green-50" : "border-gray-200 hover:border-purple-300 hover:shadow-sm"}`}
                    data-testid={`upsell-${upsell.upsell_id}`}>
                    <div className="flex items-center justify-between">
                      {upsell.image && (
                        <img src={upsell.image} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0 mr-3" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">{upsell.name}</span>
                          {isLinked && (
                            <Badge variant="outline" className="text-[9px] bg-purple-50 text-purple-600 border-purple-200">
                              <Link2 className="w-2.5 h-2.5 mr-0.5" />
                              Class
                            </Badge>
                          )}
                          {upsell.type === "product" && (
                            <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-600 border-amber-200">Product</Badge>
                          )}
                        </div>
                        {upsell.description && (
                          <p className="text-xs text-gray-500 mt-0.5">{upsell.description}</p>
                        )}
                        {isLinked && (
                          <p className="text-[10px] text-purple-500 mt-1">Your preferences auto-sync to the class dashboard</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-3">
                        {upsell.price > 0 && (
                          <span className="text-sm font-semibold text-gray-900">${upsell.price}</span>
                        )}
                        {isSelected ? (
                          <Badge className="bg-green-100 text-green-700 border-0 text-xs">
                            <Check className="w-3 h-3 mr-0.5" /> Added
                          </Badge>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSelectUpsell(upsell)}
                            disabled={selectingUpsell === upsell.upsell_id}
                            className="text-xs h-7 rounded-full"
                            data-testid={`select-upsell-${upsell.upsell_id}`}
                          >
                            {selectingUpsell === upsell.upsell_id ? "Adding..." : "Add"}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════ */}
        {/* SERVICE ADD-ON PRODUCTS - host-owned products linked to this service */}
        {/* ═══════════════════════════════════════════════════ */}
        {serviceAddons.length > 0 && (
          <div className="mb-4 bg-white rounded-2xl border border-gray-200 overflow-hidden" data-testid="service-addons-section">
            <div className="flex items-center gap-2 p-4 border-b border-gray-100">
              <ShoppingBag className="w-4 h-4 text-rose-600" />
              <span className="text-sm font-semibold text-gray-900">Recommended Add-Ons</span>
              <span className="text-[10px] text-gray-400 ml-auto">Curated by your host</span>
            </div>
            <div className="p-3 space-y-2">
              {serviceAddons.map((p, idx) => (
                <div key={p.product_id || idx}
                  className="p-3 rounded-xl border border-gray-200 hover:border-rose-300 hover:shadow-sm transition-all"
                  data-testid={`service-addon-${p.product_id || idx}`}>
                  <div className="flex items-center gap-3">
                    {p.image_url && (
                      <img src={p.image_url} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 truncate">{p.name || p.title}</span>
                        {p.brand_name && (
                          <Badge variant="outline" className="text-[9px] bg-rose-50 text-rose-600 border-rose-200">
                            {p.brand_name}
                          </Badge>
                        )}
                      </div>
                      {p.host_note && (
                        <p className="text-[11px] text-gray-500 mt-0.5 italic">"{p.host_note}"</p>
                      )}
                      {p.description && !p.host_note && (
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{p.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {p.price && (
                        <span className="text-sm font-semibold text-gray-900">${p.price}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════ */}
        {/* HOST SERVICE LINKS - Pre / During / Post Event Resources */}
        {/* ═══════════════════════════════════════════════════ */}
        {event?.resources?.length > 0 && (
          <div className="mb-4 bg-white rounded-2xl border border-gray-200 overflow-hidden" data-testid="host-service-links">
            <div className="p-3 space-y-2">
              {["pre_event", "check_in", "post_survey"].map(phase => {
                const phaseResources = event.resources.filter(r => r.category === phase);
                if (phaseResources.length === 0) return null;
                const phaseLabels = {
                  pre_event:    { label: slotText(cnf, "phase_pre_event_label",   "Before Your Appointment"),
                                   color: "text-blue-600", bg: "bg-blue-50" },
                  check_in:     { label: slotText(cnf, "phase_check_in_label",    "At Check-In"),
                                   color: "text-green-600", bg: "bg-green-50" },
                  post_survey:  { label: slotText(cnf, "phase_post_survey_label", "After Your Appointment"),
                                   color: "text-purple-600", bg: "bg-purple-50" },
                };
                const pl = phaseLabels[phase];
                const isLocked = phase === "check_in" && !isCheckedIn;
                const isPostLocked = phase === "post_survey" && rsvp?.status !== "completed" && rsvp?.status !== "checked_in";
                return (
                  <div key={phase} className={`p-3 rounded-xl ${pl.bg}`}>
                    <p className={`text-[10px] font-medium uppercase tracking-wide ${pl.color} mb-1.5`}>{pl.label}</p>
                    <div className="space-y-1.5">
                      {phaseResources.map((res, i) => (
                        <div key={i} className="flex items-center gap-2.5">
                          {res.image_url ? (
                            <img 
                              src={res.image_url} 
                              alt="" 
                              className="w-10 h-10 rounded-lg object-cover shrink-0 cursor-pointer hover:opacity-80 transition-opacity" 
                              onClick={() => setExpandedImage(res.image_url)}
                            />
                          ) : (isLocked || isPostLocked) ? (
                            <Lock className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                          ) : (
                            <ExternalLink className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            {(isLocked || isPostLocked) ? (
                              <span className="text-xs text-gray-400">{res.title} <span className="text-[10px]">(unlocks {phase === "check_in" ? "at check-in" : "after appointment"})</span></span>
                            ) : (
                              <>
                                <a href={res.link || "#"} target="_blank" rel="noopener noreferrer"
                                  className="text-xs font-medium text-gray-700 hover:underline">{res.title}</a>
                                {res.description && <p className="text-[10px] text-gray-400 truncate">{res.description}</p>}
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* CHECK IN + CAN'T MAKE IT  - side by side below preferences */}
        {rsvp?.status !== "cant_make_it" && (
          <div className="grid grid-cols-2 gap-3 mb-4">
            {event?.intake_settings?.allow_self_checkin !== false && (
              !isCheckedIn ? (
                <Button
                  onClick={handleSelfCheckIn}
                  disabled={checkingIn}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-xs py-2.5 rounded-xl"
                  data-testid="self-checkin-btn"
                >
                  {checkingIn ? (
                    <><RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />Checking in...</>
                  ) : (
                    <><Check className="w-3.5 h-3.5 mr-1.5" />Check In Now</>
                  )}
                </Button>
              ) : (
                <div className="flex items-center justify-center gap-1.5 text-xs text-green-700 bg-green-50 border border-green-200 rounded-xl py-2.5 px-3">
                  <Check className="w-3.5 h-3.5" />
                  Checked In
                </div>
              )
            )}
            <Button 
              onClick={() => setShowCantMakeItDialog(true)}
              variant="outline"
              className="text-xs py-2.5 rounded-xl border-gray-300 hover:bg-gray-50 text-gray-600 bg-white"
              data-testid="cant-make-it-btn"
            >
              <XCircle className="w-3.5 h-3.5 mr-1.5" />
              Can't Make It
            </Button>
          </div>
        )}

        {/* RESOURCES  - unlocked after check-in */}
        {resourcesData?.has_resources && (
          <div className="mb-4" data-testid="instructor-resources-section">
            {resourcesData.unlocked ? (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-4 border border-green-100">
                <div className="flex items-center gap-2 mb-3">
                  <Unlock className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-semibold text-green-800">Session Resources</span>
                  {resourcesData.instructor_name && (
                    <span className="text-xs text-green-600">from {resourcesData.instructor_name}</span>
                  )}
                </div>
                <div className="space-y-2">
                  {resourcesData.resources.map((resource, idx) => (
                    <a key={idx} href={typeof resource === 'string' ? resource : resource.url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-white rounded-xl border border-green-100 hover:bg-green-50 transition-colors" data-testid={`resource-link-${idx}`}>
                      <div className="p-2 bg-green-100 rounded-lg"><ExternalLink className="w-4 h-4 text-green-600" /></div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-gray-900 truncate">{typeof resource === 'string' ? resource : (resource.title || resource.name || resource.url)}</p>
                        {typeof resource === 'object' && resource.description && <p className="text-xs text-gray-500 truncate">{resource.description}</p>}
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    </a>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-gray-100 rounded-2xl p-4 border border-gray-200 relative overflow-hidden" data-testid="resources-locked">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-200 rounded-lg"><Lock className="w-5 h-5 text-gray-400" /></div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Session Resources</p>
                    <p className="text-xs text-gray-500">Check in to unlock resources from the instructor</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Gated Content - after check-in */}
        {event?.intake_settings?.require_checkin_for_content && isCheckedIn && (
          <div className="mb-4 space-y-4">
            <div className="flex items-center gap-2 text-green-700"><Unlock className="w-4 h-4" /><span className="text-sm font-medium">Content Unlocked</span></div>
            {event?.intake_settings?.gated_resources?.length > 0 && (
              <div className="space-y-2">
                {event.intake_settings.gated_resources.filter(r => r.title && r.url).map((resource, idx) => (
                  <a key={idx} href={resource.url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 rounded-xl border transition-colors"
                    style={{ backgroundColor: theme.accentLight, borderColor: theme.accentBorder }}>
                    <div className="p-2 rounded-lg" style={{ backgroundColor: theme.accentMedium }}><ExternalLink className="w-4 h-4" style={{ color: catIconColor }} /></div>
                    <div className="flex-1"><p className="font-medium text-sm text-gray-900">{resource.title}</p></div>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </a>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Gated Content Preview - before check-in */}
        {event?.intake_settings?.require_checkin_for_content && !isCheckedIn && (
          <div className="mb-4 p-4 bg-gray-100 rounded-xl border border-gray-200 relative overflow-hidden">
            <div className="absolute inset-0 backdrop-blur-sm bg-white/70 flex items-center justify-center z-10">
              <div className="text-center">
                <Lock className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-600">Check in to unlock</p>
              </div>
            </div>
          </div>
        )}

        {/* Tap In Playlist  - Only show if enabled in event settings */}
        {event?.tap_in_enabled !== false && event?.display_settings?.collect_vibe !== false && (
        <div className="rounded-2xl p-4 mb-4 border" style={{ background: theme.gradientLight, borderColor: theme.accentBorder }} data-testid="confirmation-tapin-card">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 shadow-sm">
              <img src={TAP_IN_COVER} alt="Tap In" className="w-full h-full object-cover" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-gray-900 text-sm mb-0.5">Tap In</h3>
              <p className="text-xs text-gray-500">Add your song to the session playlist</p>
              {event?.tap_in_invite_link || event?.playlist_link ? (
                <a href={event.tap_in_invite_link || event.playlist_link} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 text-white text-xs font-medium rounded-full transition-colors" 
                  style={{ backgroundColor: catIconColor }}
                  data-testid="tapin-playlist-link">
                  <Music className="w-3 h-3" />Open Playlist
                </a>
              ) : (
                <div className="flex items-center gap-2 mt-2 text-xs" style={{ color: catIconColor }}><Mail className="w-3 h-3" /><span>Playlist link coming soon</span></div>
              )}
            </div>
          </div>
        </div>
        )}

        {/* Preference Card Modal */}
        <UnifiedPreferenceCard
          person={{
            ...rsvp,
            profile_image: user?.profile_picture || user?.profile_image || user?.picture || rsvp?.profile_image,
            picture: user?.profile_picture || user?.profile_image || user?.picture || rsvp?.profile_image,
            dietary_restrictions: preferences?.dietary_restrictions || [],
            meat_preferences: preferences?.meat_preferences || [],
            allergies: preferences?.allergies || [],
            dietary_notes: preferences?.dietary_notes,
            accessibility_needs: preferences?.accessibility_needs || [],
            accessibility_notes: preferences?.accessibility_notes,
            invisible_disabilities: preferences?.invisible_disabilities || [],
            preferences: {
              ...preferences,
              meat_preferences: preferences?.meat_preferences || [],
              allergies: preferences?.allergies || [],
              interest_preferences: preferences?.interests || [],
              vibe: preferences?.vibe
            }
          }}
          event={{
            ...event,
            cover_image: event?.cover_image || event?.logo
          }}
          open={showPreferenceCard}
          onOpenChange={setShowPreferenceCard}
          eventId={event?.event_id}
          onEdit={handleEditPreferences}
        />

        {/* Hosted By Section with Social Media Icons */}
        {hosts.length > 0 && (
          <div className="mb-6 mt-8 pt-6 border-t border-gray-200" data-testid="hosted-by-section">
            <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3 text-center">Hosted By</h3>
            <div className="flex flex-wrap justify-center items-center gap-2 mb-3">
              {hosts.map((host, idx) => (
                <span key={host.org_id || host.id || idx} className="inline-flex items-center">
                  {host.logo && (
                    <img src={host.logo} alt={host.name} className="w-5 h-5 rounded-full object-cover mr-1.5" onError={e => e.target.style.display='none'} />
                  )}
                  {host.website ? (
                    <a 
                      href={ensureAbsoluteUrl(host.website)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-gray-700 hover:text-purple-600 transition-colors underline"
                    >
                      {host.name}
                    </a>
                  ) : (
                    <span className="text-sm font-medium text-gray-700">{host.name}</span>
                  )}
                  {idx < hosts.length - 1 && <span className="text-gray-400 px-2">&</span>}
                </span>
              ))}
            </div>
            {/* Social Media Icons for hosts */}
            <div className="flex items-center justify-center gap-3">
              {hosts.map((host) => {
                const socials = host.social_links || event?.host_social_links || {};
                return Object.entries({
                  instagram: { url: socials.instagram, icon: 'M12 2c2.717 0 3.056.01 4.122.06 1.065.05 1.79.217 2.428.465.66.254 1.216.598 1.772 1.153.509.5.902 1.105 1.153 1.772.247.637.415 1.363.465 2.428.047 1.066.06 1.405.06 4.122s-.01 3.056-.06 4.122c-.05 1.065-.218 1.79-.465 2.428a4.883 4.883 0 01-1.153 1.772c-.5.508-1.105.902-1.772 1.153-.637.247-1.363.415-2.428.465-1.066.047-1.405.06-4.122.06s-3.056-.01-4.122-.06c-1.065-.05-1.79-.218-2.428-.465a4.89 4.89 0 01-1.772-1.153 4.904 4.904 0 01-1.153-1.772c-.247-.637-.415-1.363-.465-2.428C2.013 15.056 2 14.717 2 12s.01-3.056.06-4.122c.05-1.066.217-1.79.465-2.428a4.88 4.88 0 011.153-1.772A4.897 4.897 0 015.45 2.525c.638-.248 1.362-.415 2.428-.465C8.944 2.013 9.283 2 12 2zm0 5a5 5 0 100 10 5 5 0 000-10zm6.5-.25a1.25 1.25 0 10-2.5 0 1.25 1.25 0 002.5 0zM12 9a3 3 0 110 6 3 3 0 010-6z', label: 'Instagram' },
                  linkedin: { url: socials.linkedin, icon: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452z', label: 'LinkedIn' },
                  twitter: { url: socials.twitter || socials.x, icon: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z', label: 'X' },
                  discord: { url: socials.discord, icon: 'M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03z', label: 'Discord' },
                  website: { url: socials.website || host.website, icon: null, label: 'Website' }
                }).filter(([_, v]) => v.url).map(([key, val]) => (
                  <a key={`${host.name}-${key}`} href={ensureAbsoluteUrl(val.url)} target="_blank" rel="noopener noreferrer"
                    className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                    title={val.label} data-testid={`host-social-${key}`}>
                    {val.icon ? (
                      <svg className="w-4 h-4 text-gray-600" viewBox="0 0 24 24" fill="currentColor"><path d={val.icon} /></svg>
                    ) : (
                      <Globe className="w-4 h-4 text-gray-600" />
                    )}
                  </a>
                ));
              })}
            </div>
          </div>
        )}

        {/* Host CTA Links Section */}
        {event?.cta_links && event.cta_links.length > 0 && (
          <div className="mb-6 space-y-3" data-testid="host-cta-links">
            {event.cta_links.map((cta, idx) => (
              <a key={idx} href={ensureAbsoluteUrl(cta.url)} target="_blank" rel="noopener noreferrer"
                className="block p-3 bg-white rounded-xl border border-gray-200 hover:border-purple-300 hover:shadow-md transition-all group"
                data-testid={`cta-link-${idx}`}>
                <div className="flex items-center gap-3">
                  {cta.image && (
                    <img src={cta.image} alt={cta.text} className="w-12 h-12 rounded-lg object-cover" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-gray-900 group-hover:text-purple-700 transition-colors">{cta.text}</p>
                    {cta.description && <p className="text-xs text-gray-500 truncate">{cta.description}</p>}
                  </div>
                  <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-purple-500 flex-shrink-0" />
                </div>
              </a>
            ))}
          </div>
        )}

        {/* VIP Section - if applicable */}
        {vip_info && vip_info.can_invite_guests && (
          <div className="bg-white rounded-2xl p-6 mb-6 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: theme.accentLight }}>
                <Users className="w-5 h-5" style={{ color: catIconColor }} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">
                  {vip_info.role === 'list_host' ? 'Your VIP List' : 'Invite Friends'}
                </h3>
                <p className="text-xs text-gray-500">{vip_info.list_name}</p>
              </div>
            </div>

            {vip_info.role === 'list_host' && vip_info.guest_invite_url && (
              <div className="mb-4">
                <p className="text-xs text-gray-500 mb-2">Share this link with your guests:</p>
                <div className="flex gap-2">
                  <input 
                    type="text" 
                    readOnly 
                    value={`${window.location.origin}${vip_info.guest_invite_url}`}
                    className="flex-1 text-xs bg-gray-50 border rounded-lg px-3 py-2 truncate"
                  />
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => copyInviteLink(vip_info.guest_invite_url)}
                  >
                    {inviteLinkCopied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            )}
            
            {/* Manage Guest List Button - for list hosts */}
            {vip_info.role === 'list_host' && (
              <Button
                variant="outline"
                size="sm"
                className="w-full mb-3"
                onClick={openManageGuestList}
                data-testid="manage-guest-list-btn"
              >
                <Users className="w-4 h-4 mr-2" />
                Manage Guest List
              </Button>
            )}
            
            {/* +2 Invites Section - For VIP guests (not list hosts) */}
            {vip_info.role === 'guest' && vip_info.plus_two_url && (
              <div className="rounded-lg p-4 mt-4" style={{ background: theme.gradientLight }}>
                <div className="flex items-center gap-2 mb-3">
                  <UserPlus className="w-5 h-5" style={{ color: catIconColor }} />
                  <h4 className="font-semibold" style={{ color: catIconColor }}>Your +{vip_info.max_plus_two || 2} Invites</h4>
                </div>
                
                <p className="text-sm mb-3" style={{ color: `${catIconColor}cc` }}>
                  Bring up to {vip_info.max_plus_two || 2} guests! Share your personal invite link.
                </p>
                
                {/* Stats */}
                <div className="flex items-center justify-between mb-3 text-sm">
                  <span style={{ color: catIconColor }}>
                    {vip_info.plus_two_used || 0} / {vip_info.max_plus_two || 2} used
                  </span>
                  <span className="font-medium" style={{ color: catIconColor }}>
                    {(vip_info.max_plus_two || 2) - (vip_info.plus_two_used || 0)} left
                  </span>
                </div>
                
                {/* Progress bar */}
                <div className="w-full h-2 bg-purple-100 rounded-full overflow-hidden mb-4">
                  <div 
                    className="h-full bg-purple-500 transition-all"
                    style={{ 
                      width: `${((vip_info.plus_two_used || 0) / (vip_info.max_plus_two || 2)) * 100}%` 
                    }}
                  />
                </div>
                
                {/* Share Button */}
                {(vip_info.plus_two_used || 0) < (vip_info.max_plus_two || 2) && (
                  <Button
                    className="w-full text-white"
                    style={{ backgroundColor: catIconColor }}
                    onClick={async () => {
                      const inviteUrl = `${window.location.origin}${vip_info.plus_two_url}`;
                      if (navigator.share) {
                        try {
                          await navigator.share({
                            title: 'VIP Invite',
                            text: `You're invited as my +1 guest!`,
                            url: inviteUrl
                          });
                        } catch (err) {
                          if (err.name !== 'AbortError') {
                            navigator.clipboard.writeText(inviteUrl);
                            toast.success("Invite link copied!");
                          }
                        }
                      } else {
                        navigator.clipboard.writeText(inviteUrl);
                        toast.success("Invite link copied!");
                      }
                    }}
                    data-testid="share-plus-two-btn"
                  >
                    <ArrowUp className="w-4 h-4 mr-2" />
                    Share Invite Link
                  </Button>
                )}
                
                {(vip_info.plus_two_used || 0) >= (vip_info.max_plus_two || 2) && (
                  <div className="text-center space-y-2">
                    <p className="text-sm text-purple-600 font-medium">
                      ✓ All invites used!
                    </p>
                    {!extraInviteRequested ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-purple-600 border-purple-200 hover:bg-purple-50"
                        onClick={() => setShowExtraInviteModal(true)}
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Request More Invites
                      </Button>
                    ) : (
                      <p className="text-xs text-gray-500">Request sent to host</p>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {vip_info.spots_remaining > 0 && vip_info.role === 'list_host' && (
              <p className="text-xs text-gray-500">
                {vip_info.spots_remaining} spot{vip_info.spots_remaining !== 1 ? 's' : ''} remaining
              </p>
            )}
          </div>
        )}

        {/* Footer with Sign Out */}
        <div className="text-center mt-8 pt-4 border-t border-gray-100">
          <div className="flex items-center justify-center gap-4">
            <div className="flex items-center gap-1.5 text-gray-400">
              <PhygitalIcon className="w-3.5 h-3.5 opacity-60" variant="dark" />
              <span className="text-[10px]">phygital.systems</span>
            </div>
            <button 
              onClick={() => setShowSignOutDialog(true)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              title="Sign out"
              data-testid="sign-out-btn"
            >
              <LogOut className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Image Lightbox Overlay */}
      {expandedImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setExpandedImage(null)}
          data-testid="image-lightbox"
        >
          <div className="relative max-w-lg w-full">
            <img src={expandedImage} alt="" className="w-full h-auto rounded-2xl shadow-2xl" />
            <button 
              className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 flex items-center justify-center hover:bg-white transition-colors"
              onClick={() => setExpandedImage(null)}
            >
              <X className="w-4 h-4 text-gray-700" />
            </button>
          </div>
        </div>
      )}

      {/* Sign Out Confirmation Dialog */}
      <AlertDialog open={showSignOutDialog} onOpenChange={setShowSignOutDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sign out of confirmation page?</AlertDialogTitle>
            <AlertDialogDescription>
              Your confirmation will be cleared from this device. You can always access it again using your confirmation link.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSignOut}>
              Sign Out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Can't Make It Confirmation Dialog */}
      <AlertDialog open={showCantMakeItDialog} onOpenChange={setShowCantMakeItDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel your RSVP?</AlertDialogTitle>
            <AlertDialogDescription>
              We're sorry to hear you can't make it. Your spot will be released and you'll be removed from the guest list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancellingRsvp}>Keep My RSVP</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleCantMakeIt}
              disabled={cancellingRsvp}
              className="bg-red-600 hover:bg-red-700"
            >
              {cancellingRsvp ? "Cancelling..." : "Yes, Cancel RSVP"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Manage Guest List Modal */}
      <Dialog open={showManageGuestListModal} onOpenChange={setShowManageGuestListModal}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              Manage Your Guest List
              {wsConnected && (
                <span className="flex items-center gap-1 text-xs text-green-600 font-normal ml-auto">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  Live
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              Add placeholders for guests you're inviting. They'll claim their spot when they RSVP.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4 space-y-4">
            {/* Stats */}
            {data?.vip_info && (
              <div className="bg-purple-50 rounded-lg p-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-900">{data.vip_info.list_name}</p>
                  <p className="text-xs text-purple-700">
                    {primaryVIPs.length} / {data.vip_info.max_guests || 10} VIP spots used
                    {totalPlusOnes > 0 && (
                      <span className="text-purple-500"> (+{totalPlusOnes} guests)</span>
                    )}
                  </p>
                  <p className="text-[10px] text-purple-600 mt-0.5">
                    {totalAttendeesCount} total attending
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-purple-700">
                    {(data.vip_info.max_guests || 10) - primaryVIPs.length}
                  </p>
                  <p className="text-xs text-purple-600">VIP spots left</p>
                </div>
              </div>
            )}

            {/* Progress bar */}
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all duration-300 rounded-full ${
                  primaryVIPs.length >= (data?.vip_info?.max_guests || 10)
                    ? 'bg-red-500'
                    : primaryVIPs.length >= (data?.vip_info?.max_guests || 10) * 0.8
                      ? 'bg-yellow-500'
                      : 'bg-purple-500'
                }`}
                style={{ 
                  width: `${Math.min(100, (primaryVIPs.length / (data?.vip_info?.max_guests || 10)) * 100)}%` 
                }}
              />
            </div>

            {/* Quick Share - Primary Action */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <Button
                className="w-full text-white"
                style={{ backgroundColor: catIconColor }}
                onClick={async () => {
                  const inviteUrl = `${window.location.origin}${data?.vip_info?.guest_invite_url || ''}`;
                  if (navigator.share) {
                    try {
                      await navigator.share({
                        title: `VIP Invite: ${data?.vip_info?.list_name || 'VIP List'}`,
                        text: `You're invited as a VIP guest!`,
                        url: inviteUrl
                      });
                    } catch (err) {
                      if (err.name !== 'AbortError') {
                        navigator.clipboard.writeText(inviteUrl);
                        toast.success("Invite link copied!");
                      }
                    }
                  } else {
                    navigator.clipboard.writeText(inviteUrl);
                    toast.success("Invite link copied!");
                  }
                }}
                data-testid="quick-share-btn"
              >
                <ArrowUp className="w-4 h-4 mr-2" />
                Share Invite Link
              </Button>
              <p className="text-xs text-purple-700 mt-2 text-center">
                Anyone who joins counts toward your invites
              </p>
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-gray-500">or add by name</span>
              </div>
            </div>

            {/* Add placeholder form - Simplified (name only) */}
            {primaryVIPs.length < (data?.vip_info?.max_guests || 10) && (
              <div className="flex gap-2">
                <Input
                  placeholder="Guest name"
                  value={newPlaceholderName}
                  onChange={(e) => setNewPlaceholderName(e.target.value)}
                  className="flex-1"
                  data-testid="placeholder-name-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newPlaceholderName.trim()) {
                      e.preventDefault();
                      addPlaceholderMember();
                    }
                  }}
                />
                <Button
                  onClick={addPlaceholderMember}
                  disabled={addingPlaceholder || !newPlaceholderName.trim()}
                  data-testid="add-placeholder-btn"
                >
                  {addingPlaceholder ? "..." : "+ Add"}
                </Button>
              </div>
            )}

            {/* Guest list */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">Your Guests</Label>
              {loadingGuestList ? (
                <div className="text-center py-6">
                  <div className="w-6 h-6 border-2 border-gray-200 border-t-purple-600 rounded-full animate-spin mx-auto" />
                </div>
              ) : guestListMembersToDisplay.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-6">
                  No guests added yet. Add placeholders above or share your invite link.
                </p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {guestListMembersToDisplay.map((member) => (
                    <div 
                      key={member.member_id || member.rsvp_id}
                      className="p-3 bg-white rounded-lg border space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-600">
                              {(member.name || member.display_name || "G")[0].toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {member.name || member.display_name || "Guest"}
                            </p>
                            <div className="flex items-center gap-2 flex-wrap">
                              {member.is_placeholder && !member.claimed_at ? (
                                <>
                                  <Badge className="bg-yellow-100 text-yellow-700 text-xs">Pending</Badge>
                                  {member.invite_sent && (
                                    <Badge className="bg-blue-100 text-blue-700 text-xs">Invite Sent</Badge>
                                  )}
                                </>
                              ) : (
                                <Badge className="bg-green-100 text-green-700 text-xs">Confirmed</Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        {member.is_placeholder && !member.claimed_at && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeMember(member.member_id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      
                      {/* Send Invite / Share Link for placeholders */}
                      {member.is_placeholder && !member.claimed_at && (
                        <div className="flex items-center gap-2 pt-1">
                          {/* Share individual invite link */}
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 text-xs"
                            onClick={async () => {
                              // Use the member's magic link or fall back to general invite
                              const inviteUrl = member.claim_url 
                                ? `${window.location.origin}${member.claim_url}`
                                : member.magic_link_token
                                  ? `${window.location.origin}/vip/claim/${member.magic_link_token}`
                                  : `${window.location.origin}${data?.vip_info?.guest_invite_url || ''}`;
                              
                              if (navigator.share) {
                                try {
                                  await navigator.share({
                                    title: `VIP Invite for ${member.name || member.display_name || 'You'}`,
                                    text: `You're invited to ${data?.event?.name}!`,
                                    url: inviteUrl
                                  });
                                  return;
                                } catch (err) {
                                  if (err.name !== 'AbortError') {
                                    console.log('Share failed, copying instead');
                                  } else {
                                    return;
                                  }
                                }
                              }
                              navigator.clipboard.writeText(inviteUrl);
                              toast.success("Invite link copied!");
                            }}
                          >
                            <ArrowUp className="w-3 h-3 mr-1" />
                            Share Link
                          </Button>
                          
                          {/* Send email if they have email */}
                          {member.email && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 text-xs"
                              onClick={async () => {
                                try {
                                  const token = localStorage.getItem("session_token");
                                  const res = await fetch(`${API}/events/${data.event.event_id}/vip-lists/${data.vip_info.list_id}/members/${member.member_id}/send-invite`, {
                                    method: "POST",
                                    headers: token ? { Authorization: `Bearer ${token}` } : {}
                                  });
                                  if (res.ok) {
                                    toast.success(`Invite sent to ${member.email}`);
                                    fetchGuestListMembers();
                                  } else {
                                    const err = await res.json();
                                    toast.error(err.detail || "Failed to send invite");
                                  }
                                } catch (err) {
                                  toast.error("Failed to send invite");
                                }
                              }}
                            disabled={member.invite_sent}
                          >
                            <Send className="w-3 h-3 mr-1" />
                            {member.invite_sent ? "Sent" : "Email"}
                          </Button>
                          )}
                        </div>
                      )}
                      
                      {member.email && (
                        <p className="text-xs text-gray-400 truncate">{member.email}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => setShowManageGuestListModal(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Preferences Modal - Dynamic based on event type */}
      <Dialog open={showEditPreferencesModal} onOpenChange={setShowEditPreferencesModal}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-hidden flex flex-col" data-testid="edit-preferences-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="w-5 h-5 text-purple-600" />
              Edit Your Preferences
            </DialogTitle>
            <DialogDescription>
              {isInstructorEvent 
                ? "Update your interests and accessibility preferences"
                : "Update your dietary, accessibility, and other preferences"}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4 space-y-5">
            {/* Headshot/Profile Photo - For instructor events */}
            {isInstructorEvent && (
              <div className="space-y-2" data-testid="edit-headshot-section">
                <Label className="text-sm font-medium">Profile Photo / Headshot</Label>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden">
                    {editingPreferences.headshot_url ? (
                      <img src={editingPreferences.headshot_url} alt="Headshot" className="w-full h-full object-cover" />
                    ) : (
                      <Users className="w-6 h-6 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        data-testid="headshot-upload-input"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          if (file.size > 5 * 1024 * 1024) {
                            toast.error("Image must be under 5MB");
                            return;
                          }
                          // Preview
                          const reader = new FileReader();
                          reader.onload = (ev) => {
                            setEditingPreferences(prev => ({ ...prev, headshot_url: ev.target.result }));
                          };
                          reader.readAsDataURL(file);
                          // Upload
                          try {
                            const formData = new FormData();
                            formData.append("file", file);
                            const token = localStorage.getItem("session_token");
                            const res = await fetch(`${API}/upload/headshot`, {
                              method: "POST",
                              headers: token ? { Authorization: `Bearer ${token}` } : {},
                              body: formData
                            });
                            if (res.ok) {
                              const data = await res.json();
                              setEditingPreferences(prev => ({ ...prev, headshot_url: data.url }));
                              toast.success("Photo uploaded!");
                            }
                          } catch (err) {
                            console.error("Upload error:", err);
                          }
                        }}
                      />
                      <span className="text-sm text-purple-600 hover:text-purple-700 font-medium cursor-pointer">
                        {editingPreferences.headshot_url ? "Change Photo" : "Upload Photo"}
                      </span>
                    </label>
                    <p className="text-xs text-gray-500 mt-0.5">Max 5MB, JPG or PNG</p>
                  </div>
                </div>
              </div>
            )}

            {/* About You Fields  - All submitted RSVP fields editable */}
            {isInstructorEvent && (
              <div className="space-y-3" data-testid="edit-about-section">
                <Label className="text-sm font-semibold text-gray-700">About You</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-500">Title / Role</Label>
                    <Input value={editingPreferences.title || ''} onChange={e => setEditingPreferences(prev => ({...prev, title: e.target.value}))} placeholder="e.g., Lead Developer" data-testid="edit-title" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Company</Label>
                    <Input value={editingPreferences.company || ''} onChange={e => setEditingPreferences(prev => ({...prev, company: e.target.value}))} placeholder="e.g., Emergent Labs" data-testid="edit-company" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Bio</Label>
                  <textarea
                    value={editingPreferences.bio || ''}
                    onChange={e => setEditingPreferences(prev => ({...prev, bio: e.target.value}))}
                    placeholder="Tell us about yourself..."
                    className="w-full text-sm border border-gray-200 rounded-lg p-2 h-20 resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    data-testid="edit-bio"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-500">LinkedIn</Label>
                    <Input value={editingPreferences.linkedin || ''} onChange={e => setEditingPreferences(prev => ({...prev, linkedin: e.target.value}))} placeholder="linkedin.com/in/..." data-testid="edit-linkedin" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Instagram</Label>
                    <Input value={editingPreferences.instagram || ''} onChange={e => setEditingPreferences(prev => ({...prev, instagram: e.target.value}))} placeholder="@handle" data-testid="edit-instagram" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-500">X / Twitter</Label>
                    <Input value={editingPreferences.twitter || ''} onChange={e => setEditingPreferences(prev => ({...prev, twitter: e.target.value}))} placeholder="@handle" data-testid="edit-twitter" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Website</Label>
                    <Input value={editingPreferences.website || ''} onChange={e => setEditingPreferences(prev => ({...prev, website: e.target.value}))} placeholder="https://..." data-testid="edit-website" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">What are you building?</Label>
                  <Input value={editingPreferences.what_building || ''} onChange={e => setEditingPreferences(prev => ({...prev, what_building: e.target.value}))} placeholder="Describe your project..." data-testid="edit-what-building" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-500">Stage</Label>
                    <Input value={editingPreferences.stage || ''} onChange={e => setEditingPreferences(prev => ({...prev, stage: e.target.value}))} placeholder="e.g., Pre-seed, MVP" data-testid="edit-stage" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Project Link</Label>
                    <Input value={editingPreferences.project_link || ''} onChange={e => setEditingPreferences(prev => ({...prev, project_link: e.target.value}))} placeholder="https://..." data-testid="edit-project-link" />
                  </div>
                </div>
              </div>
            )}

            {/* Interests - Always shown if enabled, using correct options per event type */}
            {data?.event?.intake_settings?.collect_interests !== false && (
              <div className="space-y-2" data-testid="edit-interests-section">
                <Label className="text-sm font-medium">Interests</Label>
                <div className="flex flex-wrap gap-2">
                  {getEditInterestOptions().map(interest => (
                    <button
                      key={interest}
                      data-testid={`interest-pill-${interest.toLowerCase().replace(/\s+/g, '-')}`}
                      onClick={() => {
                        setEditingPreferences(prev => ({
                          ...prev,
                          interests: prev.interests?.includes(interest)
                            ? prev.interests.filter(i => i !== interest)
                            : [...(prev.interests || []), interest]
                        }));
                      }}
                      className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                        editingPreferences.interests?.includes(interest)
                          ? 'bg-purple-100 border-purple-300 text-purple-700'
                          : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {interest}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Dietary Restrictions - Only for events that collect dietary */}
            {showDietaryInEdit && (
              <>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Dietary Restrictions</Label>
                  <div className="flex flex-wrap gap-2">
                    {["Vegetarian", "Vegan", "Gluten-Free", "Dairy-Free", "Nut Allergy", "Kosher", "Halal"].map(diet => (
                      <button
                        key={diet}
                        onClick={() => {
                          setEditingPreferences(prev => ({
                            ...prev,
                            dietary_restrictions: prev.dietary_restrictions?.includes(diet)
                              ? prev.dietary_restrictions.filter(d => d !== diet)
                              : [...(prev.dietary_restrictions || []), diet]
                          }));
                        }}
                        className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                          editingPreferences.dietary_restrictions?.includes(diet)
                            ? 'bg-green-100 border-green-300 text-green-700'
                            : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {diet}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Protein Preferences</Label>
                  <div className="flex flex-wrap gap-2">
                    {["Chicken", "Beef", "Pork", "Lamb", "Fish", "Shellfish", "No Meat"].map(meat => (
                      <button
                        key={meat}
                        onClick={() => {
                          setEditingPreferences(prev => ({
                            ...prev,
                            meat_preferences: prev.meat_preferences?.includes(meat)
                              ? prev.meat_preferences.filter(m => m !== meat)
                              : [...(prev.meat_preferences || []), meat]
                          }));
                        }}
                        className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                          editingPreferences.meat_preferences?.includes(meat)
                            ? 'bg-orange-100 border-orange-300 text-orange-700'
                            : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {meat}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Allergies</Label>
                  <div className="flex flex-wrap gap-2">
                    {["Peanuts", "Tree Nuts", "Shellfish", "Fish", "Eggs", "Milk", "Soy", "Wheat", "Sesame"].map(allergy => (
                      <button
                        key={allergy}
                        onClick={() => {
                          setEditingPreferences(prev => ({
                            ...prev,
                            allergies: prev.allergies?.includes(allergy)
                              ? prev.allergies.filter(a => a !== allergy)
                              : [...(prev.allergies || []), allergy]
                          }));
                        }}
                        className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                          editingPreferences.allergies?.includes(allergy)
                            ? 'bg-red-100 border-red-300 text-red-700'
                            : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {allergy}
                      </button>
                    ))}
                  </div>
                  <Input
                    placeholder="Other dietary notes..."
                    value={editingPreferences.dietary_notes || ""}
                    onChange={(e) => setEditingPreferences(prev => ({ ...prev, dietary_notes: e.target.value }))}
                    className="mt-2"
                  />
                </div>
              </>
            )}

            {/* Accessibility Needs */}
            {data?.event?.intake_settings?.collect_accessibility !== false && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  {isInstructorEvent ? "Digital Accessibility Needs" : "Accessibility Needs"}
                </Label>
                <div className="flex flex-wrap gap-2">
                  {(isInstructorEvent 
                    ? ["Screen Reader", "Captions/Subtitles", "Color Blindness", "Large Text", "ADHD/Focus Support", "Sign Language"]
                    : ["Wheelchair Access", "Sign Language", "Visual Assistance", "Hearing Assistance", "Quiet Space", "Service Animal"]
                  ).map(need => (
                    <button
                      key={need}
                      onClick={() => {
                        setEditingPreferences(prev => ({
                          ...prev,
                          accessibility_needs: prev.accessibility_needs?.includes(need)
                            ? prev.accessibility_needs.filter(n => n !== need)
                            : [...(prev.accessibility_needs || []), need]
                        }));
                      }}
                      className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                        editingPreferences.accessibility_needs?.includes(need)
                          ? 'bg-blue-100 border-blue-300 text-blue-700'
                          : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {need}
                    </button>
                  ))}
                </div>
                <Input
                  placeholder="Other accessibility notes..."
                  value={editingPreferences.accessibility_notes || ""}
                  onChange={(e) => setEditingPreferences(prev => ({ ...prev, accessibility_notes: e.target.value }))}
                  className="mt-2"
                />
              </div>
            )}

            {/* Invisible Disabilities */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Invisible Disabilities</Label>
              <p className="text-xs text-gray-500 mb-2">Optional - helps hosts provide better accommodations</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: 'vocal_tics', label: 'Vocal tics', desc: 'I may make sudden sounds or verbal expressions' },
                  { id: 'motor_tics', label: 'Motor tics', desc: 'I may have sudden movements or gestures' },
                  { id: 'tourettes', label: "Tourette's Syndrome", desc: 'Combination of vocal and motor tics' },
                  { id: 'anxiety', label: 'Anxiety', desc: 'I may need extra patience or breaks' },
                  { id: 'chronic_pain', label: 'Chronic pain', desc: 'I may need to adjust position frequently' },
                  { id: 'chronic_fatigue', label: 'Chronic fatigue', desc: 'I may need rest breaks during the event' },
                  { id: 'adhd', label: 'ADHD', desc: 'I may need reminders or have difficulty with long sessions' },
                  { id: 'autism', label: 'Autism spectrum', desc: 'I may have specific sensory or communication preferences' },
                  { id: 'epilepsy', label: 'Epilepsy', desc: 'Please be aware of potential seizures' }
                ].map(option => (
                  <button
                    key={option.id}
                    onClick={() => {
                      setEditingPreferences(prev => ({
                        ...prev,
                        invisible_disabilities: prev.invisible_disabilities?.includes(option.id)
                          ? prev.invisible_disabilities.filter(c => c !== option.id)
                          : [...(prev.invisible_disabilities || []), option.id]
                      }));
                    }}
                    className={`p-2.5 rounded-lg border text-left transition-colors ${
                      editingPreferences.invisible_disabilities?.includes(option.id)
                        ? 'bg-purple-50 border-purple-300'
                        : 'bg-white border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <p className="text-sm font-medium">{option.label}</p>
                    <p className="text-xs text-gray-500 line-clamp-1">{option.desc}</p>
                  </button>
                ))}
              </div>
              <Input
                placeholder="Anything else we should know (optional)..."
                value={editingPreferences.anything_else_to_share || ""}
                onChange={(e) => setEditingPreferences(prev => ({ ...prev, anything_else_to_share: e.target.value }))}
                className="mt-2"
              />
            </div>
          </div>

          <DialogFooter className="pt-4 border-t gap-2">
            <Button variant="outline" onClick={() => setShowEditPreferencesModal(false)}>
              Cancel
            </Button>
            <Button onClick={savePreferences} disabled={savingPreferences} data-testid="save-preferences-btn">
              {savingPreferences ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extra Invite Request Modal */}
      <Dialog open={showExtraInviteModal} onOpenChange={setShowExtraInviteModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Request Extra Invites</DialogTitle>
            <DialogDescription>
              Ask the host for more +2 invite spots
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                How many extra invites do you need?
              </label>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setExtraInviteCount(Math.max(1, extraInviteCount - 1))}
                >
                  -
                </Button>
                <span className="text-xl font-bold w-8 text-center">{extraInviteCount}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setExtraInviteCount(Math.min(10, extraInviteCount + 1))}
                >
                  +
                </Button>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Reason (optional)
              </label>
              <textarea
                value={extraInviteReason}
                onChange={(e) => setExtraInviteReason(e.target.value)}
                placeholder="Let the host know why you need more invites..."
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none"
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowExtraInviteModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={requestExtraInvites} 
              disabled={requestingExtraInvites}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {requestingExtraInvites ? "Sending..." : "Send Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
